import React, { useState, useEffect, useCallback, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { fetchDepositAddress, fetchWallet, fetchMyPositions, requestCredit, getCreditStatus } from "./client";
import { useI18n } from "./i18n";


const DPT = {
  /* ─── Modal header / tabs ─────────────────────────────────────────── */
  title:    { fr:"Alimenter",                en:"Fund",                 es:"Financiar",            de:"Aufladen",             pt:"Financiar",           ar:"تمويل"           },
  title2:   { fr:"le compte",                en:"Account",              es:"la cuenta",            de:"Konto",                pt:"a conta",             ar:"الحساب"          },
  sub:      { fr:"Choisissez votre méthode de dépôt", en:"Choose your deposit method", es:"Elige tu método de depósito", de:"Zahlungsmethode wählen", pt:"Escolha seu método de depósito", ar:"اختر طريقة الإيداع" },
  crypto:   { fr:"Crypto",                   en:"Crypto",               es:"Cripto",               de:"Krypto",               pt:"Cripto",              ar:"كريبتو"          },
  card:     { fr:"Carte",                    en:"Card",                 es:"Tarjeta",              de:"Karte",                pt:"Cartão",              ar:"بطاقة"           },
  mobile:   { fr:"Mobile",                   en:"Mobile",               es:"Móvil",                de:"Mobil",                pt:"Móvel",               ar:"موبايل"          },
  bank:     { fr:"Virement",                 en:"Bank",                 es:"Transferencia",        de:"Überweisung",          pt:"Transferência",       ar:"تحويل"           },
  boost:    { fr:"Crédit",                   en:"Credit",               es:"Crédito",              de:"Kredit",               pt:"Crédito",             ar:"ائتمان"          },
  close:    { fr:"Fermer",                   en:"Close",                es:"Cerrar",               de:"Schließen",            pt:"Fechar",              ar:"إغلاق"           },

  /* ─── Crypto block ────────────────────────────────────────────────── */
  selectCrypto: { fr:"Sélectionner la crypto",    en:"Select crypto",          es:"Seleccionar cripto",     de:"Krypto auswählen",      pt:"Selecionar cripto",        ar:"اختر العملة الرقمية" },
  network:      { fr:"Réseau",                    en:"Network",                es:"Red",                    de:"Netzwerk",              pt:"Rede",                     ar:"الشبكة"          },
  minDep:       { fr:"Dépôt minimum",             en:"Minimum deposit",        es:"Depósito mínimo",        de:"Mindesteinzahlung",     pt:"Depósito mínimo",          ar:"الحد الأدنى للإيداع"},
  confirmations:{ fr:"Confirmations requises",    en:"Required confirmations", es:"Confirmaciones requeridas", de:"Erforderliche Bestätigungen", pt:"Confirmações necessárias", ar:"التأكيدات المطلوبة" },
  copy:         { fr:"Copier",                    en:"Copy",                   es:"Copiar",                 de:"Kopieren",              pt:"Copiar",                   ar:"نسخ"             },
  copied:       { fr:"✓ Copié",                   en:"✓ Copied",               es:"✓ Copiado",              de:"✓ Kopiert",             pt:"✓ Copiado",                ar:"✓ تم النسخ"     },
  genAddr:      { fr:"Génération de l'adresse…",  en:"Generating address…",    es:"Generando dirección…",   de:"Adresse generieren…",   pt:"Gerando endereço…",        ar:"جارٍ إنشاء العنوان…" },
  cryptoNote1:  {
    fr:"Envoyez uniquement du {coin} sur le réseau {network}. Tout envoi incorrect entraîne une perte définitive.",
    en:"Send only {coin} on the {network} network. Any incorrect transfer results in permanent loss.",
    es:"Envíe únicamente {coin} en la red {network}. Cualquier envío incorrecto provocará una pérdida definitiva.",
    de:"Senden Sie ausschließlich {coin} im {network}-Netzwerk. Falsche Überweisungen führen zum endgültigen Verlust.",
    pt:"Envie apenas {coin} na rede {network}. Qualquer envio incorreto resultará em perda definitiva.",
    ar:"أرسل فقط {coin} على شبكة {network}. أي تحويل خاطئ يؤدي إلى خسارة دائمة.",
  },
  cryptoNote2: {
    fr:"Les dépôts sont crédités après {n} confirmations on-chain (5–30 min).",
    en:"Deposits are credited after {n} on-chain confirmations (5–30 min).",
    es:"Los depósitos se acreditan tras {n} confirmaciones on-chain (5–30 min).",
    de:"Einzahlungen werden nach {n} On-Chain-Bestätigungen gutgeschrieben (5–30 Min).",
    pt:"Os depósitos são creditados após {n} confirmações on-chain (5–30 min).",
    ar:"يتم إيداع المبالغ بعد {n} تأكيدات على السلسلة (5–30 دقيقة).",
  },

  /* ─── Amounts / generic ───────────────────────────────────────────── */
  amountUSD:        { fr:"Montant (USD)",         en:"Amount (USD)",       es:"Monto (USD)",       de:"Betrag (USD)",       pt:"Valor (USD)",        ar:"المبلغ (دولار)"  },
  amountCustom:     { fr:"Montant personnalisé ($)", en:"Custom amount ($)", es:"Monto personalizado ($)", de:"Eigener Betrag ($)", pt:"Valor personalizado ($)", ar:"مبلغ مخصص (دولار)" },
  total:            { fr:"Total",                 en:"Total",              es:"Total",             de:"Gesamt",             pt:"Total",              ar:"المجموع"        },
  included:         { fr:"Inclus",                en:"Included",           es:"Incluido",          de:"Inklusive",          pt:"Incluído",           ar:"شامل"           },
  free:             { fr:"Gratuit",               en:"Free",               es:"Gratis",            de:"Kostenlos",          pt:"Grátis",             ar:"مجاني"          },

  /* ─── Stripe ──────────────────────────────────────────────────────── */
  stripeNotConfig:  { fr:"Stripe non configuré",  en:"Stripe not configured", es:"Stripe no configurado", de:"Stripe nicht konfiguriert", pt:"Stripe não configurado", ar:"Stripe غير مُهيأ" },
  stripeNotConfigDesc: {
    fr:"Ajoutez votre clé publique dans le fichier .env :",
    en:"Add your public key to the .env file:",
    es:"Añade tu clave pública en el archivo .env:",
    de:"Fügen Sie Ihren öffentlichen Schlüssel in die .env-Datei ein:",
    pt:"Adicione sua chave pública no arquivo .env:",
    ar:"أضف مفتاحك العام في ملف .env:",
  },
  cardInfo:         { fr:"Informations de carte", en:"Card information",   es:"Información de la tarjeta", de:"Karteninformationen", pt:"Informações do cartão", ar:"معلومات البطاقة" },
  cardNumber:       { fr:"Numéro de carte",       en:"Card number",        es:"Número de tarjeta", de:"Kartennummer",       pt:"Número do cartão",   ar:"رقم البطاقة"     },
  cardName:         { fr:"Nom sur la carte",      en:"Cardholder name",    es:"Nombre en la tarjeta", de:"Name auf der Karte", pt:"Nome no cartão",   ar:"الاسم على البطاقة" },
  cardNamePh:       { fr:"JEAN DUPONT",           en:"JOHN DOE",           es:"JUAN PEREZ",        de:"MAX MUSTERMANN",     pt:"JOÃO SILVA",         ar:"JOHN DOE"        },
  cardExpiry:       { fr:"Expiration",            en:"Expiry",             es:"Caducidad",         de:"Ablaufdatum",        pt:"Validade",           ar:"تاريخ الانتهاء"  },
  cardExpiryPh:     { fr:"MM/AA",                 en:"MM/YY",              es:"MM/AA",             de:"MM/JJ",              pt:"MM/AA",              ar:"MM/YY"           },
  cvc:              { fr:"CVC",                   en:"CVC",                es:"CVC",               de:"CVC",                pt:"CVC",                ar:"CVC"             },
  stripeTestCards:  { fr:"Cartes de test Stripe", en:"Stripe test cards",  es:"Tarjetas de prueba Stripe", de:"Stripe-Testkarten", pt:"Cartões de teste Stripe", ar:"بطاقات اختبار Stripe" },
  stripeAccepted:   { fr:"✓ Paiement accepté",    en:"✓ Payment accepted", es:"✓ Pago aceptado",   de:"✓ Zahlung akzeptiert", pt:"✓ Pagamento aceito", ar:"✓ تم قبول الدفع" },
  stripeRefused:    { fr:"✕ Carte refusée",       en:"✕ Card declined",    es:"✕ Tarjeta rechazada", de:"✕ Karte abgelehnt", pt:"✕ Cartão recusado",  ar:"✕ تم رفض البطاقة" },
  stripe3ds:        { fr:"⚡ Authentification 3DS", en:"⚡ 3DS authentication", es:"⚡ Autenticación 3DS", de:"⚡ 3DS-Authentifizierung", pt:"⚡ Autenticação 3DS", ar:"⚡ مصادقة 3DS"     },
  stripeTestHint:   {
    fr:"Expiry : n'importe quelle date future · CVC : n'importe quels 3 chiffres",
    en:"Expiry: any future date · CVC: any 3 digits",
    es:"Caducidad: cualquier fecha futura · CVC: 3 dígitos cualesquiera",
    de:"Ablauf: beliebiges zukünftiges Datum · CVC: beliebige 3 Ziffern",
    pt:"Validade: qualquer data futura · CVC: 3 dígitos quaisquer",
    ar:"الانتهاء: أي تاريخ مستقبلي · CVC: أي 3 أرقام",
  },
  stripeFee:        { fr:"Frais Stripe (2.9% + $0.30)", en:"Stripe fees (2.9% + $0.30)", es:"Comisión Stripe (2,9% + $0,30)", de:"Stripe-Gebühren (2,9% + $0,30)", pt:"Taxas Stripe (2,9% + $0,30)", ar:"رسوم Stripe (2.9% + 0.30$)" },
  stripeTotalCharged: { fr:"Total débité",        en:"Total charged",      es:"Total cobrado",     de:"Belasteter Gesamtbetrag", pt:"Total cobrado",  ar:"المبلغ الإجمالي المُحصَّل" },
  stripeWalletCredit: { fr:"Crédité sur votre wallet", en:"Credited to your wallet", es:"Acreditado en tu wallet", de:"Auf Ihre Wallet gutgeschrieben", pt:"Creditado na sua carteira", ar:"تم الإيداع في محفظتك" },
  processing:       { fr:"Traitement en cours…",  en:"Processing…",        es:"Procesando…",       de:"Wird verarbeitet…",  pt:"Processando…",       ar:"جاري المعالجة…" },
  payBtn:           {
    fr:"Payer ${amount}",
    en:"Pay ${amount}",
    es:"Pagar ${amount}",
    de:"${amount} bezahlen",
    pt:"Pagar ${amount}",
    ar:"ادفع ${amount}",
  },
  stripeSecured:    { fr:"Paiement sécurisé par Stripe", en:"Payment secured by Stripe", es:"Pago seguro por Stripe", de:"Sichere Zahlung mit Stripe", pt:"Pagamento seguro pela Stripe", ar:"دفع آمن بواسطة Stripe" },
  stripeNote1:      {
    fr:"Vos coordonnées bancaires sont envoyées directement à Stripe (PCI DSS Level 1). Nos serveurs ne les voient jamais.",
    en:"Your card details are sent directly to Stripe (PCI DSS Level 1). Our servers never see them.",
    es:"Sus datos bancarios se envían directamente a Stripe (PCI DSS Nivel 1). Nuestros servidores nunca los ven.",
    de:"Ihre Kartendaten werden direkt an Stripe übertragen (PCI DSS Level 1). Unsere Server sehen sie nie.",
    pt:"Os dados do seu cartão são enviados diretamente para a Stripe (PCI DSS Nível 1). Nossos servidores nunca os veem.",
    ar:"يتم إرسال بيانات بطاقتك مباشرة إلى Stripe (PCI DSS مستوى 1). خوادمنا لا تراها أبدًا.",
  },
  stripeNote2:      {
    fr:"Authentification 3D Secure automatique si requise par votre banque.",
    en:"Automatic 3D Secure authentication if required by your bank.",
    es:"Autenticación 3D Secure automática si lo requiere tu banco.",
    de:"Automatische 3D-Secure-Authentifizierung, falls von Ihrer Bank gefordert.",
    pt:"Autenticação 3D Secure automática se exigida pelo seu banco.",
    ar:"مصادقة 3D Secure تلقائية إذا طلبها مصرفك.",
  },
  paymentFailed:    { fr:"Le paiement a échoué.", en:"Payment failed.",   es:"El pago falló.",    de:"Zahlung fehlgeschlagen.", pt:"O pagamento falhou.", ar:"فشل الدفع." },
  serverError:      { fr:"Erreur serveur.",       en:"Server error.",      es:"Error del servidor.", de:"Serverfehler.",     pt:"Erro do servidor.",  ar:"خطأ في الخادم." },

  /* ─── Tingg / Mobile Money ────────────────────────────────────────── */
  country:          { fr:"Pays",                   en:"Country",            es:"País",              de:"Land",               pt:"País",               ar:"الدولة"        },
  countryAll:       { fr:"🌍 Tous",                en:"🌍 All",             es:"🌍 Todos",          de:"🌍 Alle",            pt:"🌍 Todos",           ar:"🌍 الكل"        },
  operator:         { fr:"opérateur",              en:"operator",           es:"operador",          de:"Anbieter",           pt:"operadora",          ar:"مشغّل"          },
  operators:        { fr:"opérateurs",             en:"operators",          es:"operadores",        de:"Anbieter",           pt:"operadoras",         ar:"مشغّلين"        },
  operatorsAvailable: { fr:"Opérateurs disponibles via Tingg", en:"Operators available via Tingg", es:"Operadores disponibles a través de Tingg", de:"Über Tingg verfügbare Anbieter", pt:"Operadoras disponíveis via Tingg", ar:"المشغّلون المتاحون عبر Tingg" },
  operatorMobile:   { fr:"Opérateur Mobile Money", en:"Mobile Money operator", es:"Operador Mobile Money", de:"Mobile-Money-Anbieter", pt:"Operadora Mobile Money", ar:"مشغّل Mobile Money" },
  phoneNumber:      { fr:"Numéro Mobile Money",    en:"Mobile Money number", es:"Número Mobile Money", de:"Mobile-Money-Nummer", pt:"Número Mobile Money", ar:"رقم Mobile Money" },
  feeTingg:         { fr:"Frais Tingg",            en:"Tingg fees",         es:"Comisión Tingg",    de:"Tingg-Gebühren",     pt:"Taxas Tingg",        ar:"رسوم Tingg"     },
  initTingg:        { fr:"Initialisation Tingg…",  en:"Initializing Tingg…", es:"Inicializando Tingg…", de:"Tingg wird initialisiert…", pt:"Inicializando Tingg…", ar:"جاري تهيئة Tingg…" },
  payViaTingg:      {
    fr:"Payer via Tingg — {country}",
    en:"Pay via Tingg — {country}",
    es:"Pagar via Tingg — {country}",
    de:"Mit Tingg bezahlen — {country}",
    pt:"Pagar via Tingg — {country}",
    ar:"ادفع عبر Tingg — {country}",
  },
  selectCountry:    { fr:"Sélectionnez votre pays", en:"Select your country", es:"Selecciona tu país", de:"Land auswählen",   pt:"Selecione seu país", ar:"اختر بلدك"      },
  tinggDesc:        { fr:"Tingg by Cellulant",     en:"Tingg by Cellulant", es:"Tingg by Cellulant", de:"Tingg by Cellulant", pt:"Tingg by Cellulant", ar:"Tingg by Cellulant" },
  tinggReach:       { fr:"33+ pays · 200+ opérateurs", en:"33+ countries · 200+ operators", es:"33+ países · 200+ operadores", de:"33+ Länder · 200+ Anbieter", pt:"33+ países · 200+ operadoras", ar:"33+ دولة · 200+ مشغّل" },
  tinggCheckPhone:  { fr:"Vérifiez votre téléphone", en:"Check your phone", es:"Comprueba tu teléfono", de:"Telefon prüfen",   pt:"Verifique seu telefone", ar:"تحقق من هاتفك" },
  tinggNotifSent:   {
    fr:"Une notification Tingg a été envoyée au",
    en:"A Tingg notification was sent to",
    es:"Se envió una notificación Tingg a",
    de:"Eine Tingg-Benachrichtigung wurde gesendet an",
    pt:"Uma notificação Tingg foi enviada para",
    ar:"تم إرسال إشعار Tingg إلى",
  },
  tinggAccept:      {
    fr:"Acceptez la demande et entrez votre PIN Mobile Money.",
    en:"Accept the request and enter your Mobile Money PIN.",
    es:"Acepta la solicitud e introduce tu PIN Mobile Money.",
    de:"Akzeptieren Sie die Anfrage und geben Sie Ihre Mobile-Money-PIN ein.",
    pt:"Aceite a solicitação e digite seu PIN Mobile Money.",
    ar:"اقبل الطلب وأدخل رمز PIN الخاص بـ Mobile Money.",
  },
  tinggStep1:       { fr:"Ouvrez la notification sur votre téléphone", en:"Open the notification on your phone", es:"Abre la notificación en tu teléfono", de:"Öffnen Sie die Benachrichtigung auf Ihrem Telefon", pt:"Abra a notificação no seu telefone", ar:"افتح الإشعار على هاتفك" },
  tinggStep2:       { fr:"Entrez votre PIN Mobile Money", en:"Enter your Mobile Money PIN", es:"Introduce tu PIN Mobile Money", de:"Geben Sie Ihre Mobile-Money-PIN ein", pt:"Digite seu PIN Mobile Money", ar:"أدخل رمز PIN الخاص بـ Mobile Money" },
  tinggStep3:       { fr:"Confirmez le paiement", en:"Confirm the payment", es:"Confirma el pago", de:"Bestätigen Sie die Zahlung", pt:"Confirme o pagamento", ar:"أكّد الدفع" },
  tinggConfirmed:   { fr:"✓ J'ai confirmé le paiement", en:"✓ I confirmed the payment", es:"✓ He confirmado el pago", de:"✓ Ich habe die Zahlung bestätigt", pt:"✓ Confirmei o pagamento", ar:"✓ لقد أكّدت الدفع" },
  tinggRestart:     { fr:"← Recommencer",          en:"← Restart",          es:"← Reiniciar",       de:"← Neu starten",      pt:"← Reiniciar",        ar:"← إعادة المحاولة" },
  tinggInitFail:    { fr:"Échec de l'initialisation Tingg.", en:"Failed to initialize Tingg.", es:"Error al inicializar Tingg.", de:"Tingg-Initialisierung fehlgeschlagen.", pt:"Falha ao inicializar Tingg.", ar:"فشل في تهيئة Tingg." },
  tinggConvert:     {
    fr:"{amount} USD → converti en {currency} par Tingg",
    en:"{amount} USD → converted to {currency} by Tingg",
    es:"{amount} USD → convertido a {currency} por Tingg",
    de:"{amount} USD → von Tingg in {currency} umgerechnet",
    pt:"{amount} USD → convertido para {currency} pela Tingg",
    ar:"{amount} دولار → يُحوَّل إلى {currency} بواسطة Tingg",
  },
  tinggNote1: {
    fr:"Tingg initie un push de paiement sur votre téléphone. Aucun code à saisir ici.",
    en:"Tingg sends a payment push to your phone. No code to enter here.",
    es:"Tingg envía una solicitud de pago a tu teléfono. No hay que introducir ningún código aquí.",
    de:"Tingg sendet eine Zahlungsanfrage an Ihr Telefon. Kein Code hier einzugeben.",
    pt:"O Tingg envia uma solicitação de pagamento ao seu telefone. Nenhum código a inserir aqui.",
    ar:"يُرسل Tingg إشعار دفع إلى هاتفك. لا حاجة لإدخال أي رمز هنا.",
  },
  tinggNote2: { fr:"Crédité instantanément après confirmation.", en:"Credited instantly after confirmation.", es:"Acreditado al instante tras la confirmación.", de:"Sofort nach Bestätigung gutgeschrieben.", pt:"Creditado instantaneamente após confirmação.", ar:"يُودَع فورًا بعد التأكيد." },
  tinggNote3: { fr:"Tingg convertit automatiquement depuis votre devise locale vers l'USD.", en:"Tingg automatically converts from your local currency to USD.", es:"Tingg convierte automáticamente desde tu moneda local a USD.", de:"Tingg rechnet automatisch von Ihrer lokalen Währung in USD um.", pt:"O Tingg converte automaticamente da sua moeda local para USD.", ar:"يُحوّل Tingg تلقائيًا من عملتك المحلية إلى الدولار الأمريكي." },

  /* ─── Credit Boost ────────────────────────────────────────────────── */
  creditApproved:   { fr:"Crédit accordé !",       en:"Credit approved!",   es:"¡Crédito concedido!", de:"Kredit gewährt!",    pt:"Crédito aprovado!", ar:"تمت الموافقة على الائتمان!" },
  creditPending:    { fr:"Demande en cours",       en:"Request pending",    es:"Solicitud en curso", de:"Anfrage läuft",      pt:"Solicitação em andamento", ar:"الطلب قيد المعالجة" },
  creditApprovedDesc: {
    fr:"Votre compte a été crédité de $100 USDC.",
    en:"Your account has been credited with $100 USDC.",
    es:"Tu cuenta ha sido acreditada con $100 USDC.",
    de:"Ihr Konto wurde mit $100 USDC gutgeschrieben.",
    pt:"Sua conta foi creditada com $100 USDC.",
    ar:"تم إيداع 100 دولار USDC في حسابك.",
  },
  creditPendingDesc: {
    fr:"Votre demande de crédit est en cours de traitement. Vous serez crédité sous 24h.",
    en:"Your credit request is being processed. You will be credited within 24h.",
    es:"Tu solicitud de crédito está en proceso. Serás acreditado en 24h.",
    de:"Ihre Kreditanfrage wird bearbeitet. Sie werden innerhalb von 24 Stunden gutgeschrieben.",
    pt:"Sua solicitação de crédito está em processamento. Você será creditado em 24h.",
    ar:"يتم معالجة طلب الائتمان الخاص بك. سيتم إيداع المبلغ خلال 24 ساعة.",
  },
  statusApproved:   { fr:"✓ Approuvé",             en:"✓ Approved",         es:"✓ Aprobado",        de:"✓ Genehmigt",        pt:"✓ Aprovado",         ar:"✓ مُوافَق عليه" },
  statusPending:    { fr:"⏳ En attente",          en:"⏳ Pending",          es:"⏳ Pendiente",       de:"⏳ Ausstehend",       pt:"⏳ Pendente",         ar:"⏳ قيد الانتظار" },
  timerLabel:       { fr:"Temps restant avant crédit", en:"Time remaining before credit", es:"Tiempo restante antes del crédito", de:"Verbleibende Zeit bis zur Gutschrift", pt:"Tempo restante até o crédito", ar:"الوقت المتبقي قبل الإيداع" },
  processingLabel:  { fr:"Traitement en cours…",   en:"Processing…",        es:"Procesando…",       de:"Wird verarbeitet…",  pt:"Processando…",       ar:"جاري المعالجة…" },
  requestSentOn:    { fr:"Demande envoyée le",     en:"Request sent on",    es:"Solicitud enviada el", de:"Anfrage gesendet am", pt:"Solicitação enviada em", ar:"تم إرسال الطلب في" },
  amountLabel:      { fr:"Montant",                en:"Amount",             es:"Monto",             de:"Betrag",             pt:"Valor",              ar:"المبلغ"        },
  doneClose:        { fr:"✓ Fermer",               en:"✓ Close",            es:"✓ Cerrar",          de:"✓ Schließen",        pt:"✓ Fechar",           ar:"✓ إغلاق"       },
  boostTitle:       { fr:"Crédit de démarrage",    en:"Starter credit",     es:"Crédito de inicio", de:"Startguthaben",      pt:"Crédito inicial",    ar:"رصيد البداية"  },
  boostDesc:        {
    fr:"Si votre solde est inférieur à 100 EVP et que vous n'avez aucun trade en cours, vous pouvez demander un crédit pour atteindre 100 EVP.",
    en:"If your balance is below 100 EVP and you have no active trades, you can request a credit to reach 100 EVP.",
    es:"Si tu saldo es inferior a 100 EVP y no tienes ningún trade en curso, puedes solicitar un crédito para alcanzar los 100 EVP.",
    de:"Wenn Ihr Guthaben unter 100 EVP liegt und Sie keine offenen Trades haben, können Sie eine Gutschrift beantragen, um 100 EVP zu erreichen.",
    pt:"Se o seu saldo for inferior a 100 EVP e você não tiver nenhum trade em andamento, pode solicitar um crédito para atingir 100 EVP.",
    ar:"إذا كان رصيدك أقل من 100 EVP وليس لديك أي صفقات نشطة، يمكنك طلب رصيد للوصول إلى 100 EVP.",
  },
  boostAmountSub:   { fr:"Montant qui sera crédité sur votre compte", en:"Amount that will be credited to your account", es:"Monto que se acreditará en tu cuenta", de:"Betrag, der Ihrem Konto gutgeschrieben wird", pt:"Valor que será creditado na sua conta", ar:"المبلغ الذي سيتم إيداعه في حسابك" },
  eligibility:      { fr:"Conditions d'éligibilité", en:"Eligibility conditions", es:"Condiciones de elegibilidad", de:"Berechtigungsvoraussetzungen", pt:"Condições de elegibilidade", ar:"شروط الأهلية" },
  condBalance:      { fr:"Solde inférieur à 100 EVP", en:"Balance below 100 EVP", es:"Saldo inferior a 100 EVP", de:"Guthaben unter 100 EVP", pt:"Saldo inferior a 100 EVP", ar:"رصيد أقل من 100 EVP" },
  condBalanceCur:   {
    fr:"(actuellement : {amount} EVP)",
    en:"(currently: {amount} EVP)",
    es:"(actualmente: {amount} EVP)",
    de:"(aktuell: {amount} EVP)",
    pt:"(atualmente: {amount} EVP)",
    ar:"(حالياً: {amount} EVP)",
  },
  condNoTrades:     { fr:"Aucun trade en cours",   en:"No active trades",   es:"Ningún trade en curso", de:"Keine aktiven Trades", pt:"Nenhum trade em andamento", ar:"لا توجد صفقات نشطة" },
  positionOne:      { fr:"position ouverte",       en:"open position",      es:"posición abierta",  de:"offene Position",    pt:"posição aberta",     ar:"مركز مفتوح"    },
  positionMany:     { fr:"positions ouvertes",     en:"open positions",     es:"posiciones abiertas", de:"offene Positionen", pt:"posições abertas",  ar:"مراكز مفتوحة"  },
  processingTime:   { fr:"Délai de traitement",    en:"Processing time",    es:"Plazo de tramitación", de:"Bearbeitungszeit", pt:"Prazo de processamento", ar:"مدة المعالجة" },
  within24h:        { fr:"Sous 24 heures",         en:"Within 24 hours",    es:"En menos de 24 horas", de:"Innerhalb von 24 Stunden", pt:"Em até 24 horas",  ar:"خلال 24 ساعة"   },
  warnBoth:         {
    fr:"⚠ Vous avez un solde ≥ 100 EVP et des trades en cours. Les deux conditions doivent être remplies.",
    en:"⚠ Your balance is ≥ 100 EVP and you have active trades. Both conditions must be met.",
    es:"⚠ Tu saldo es ≥ 100 EVP y tienes trades en curso. Deben cumplirse ambas condiciones.",
    de:"⚠ Ihr Guthaben beträgt ≥ 100 EVP und Sie haben aktive Trades. Beide Bedingungen müssen erfüllt sein.",
    pt:"⚠ Seu saldo é ≥ 100 EVP e você tem trades em andamento. Ambas as condições devem ser atendidas.",
    ar:"⚠ رصيدك ≥ 100 EVP ولديك صفقات نشطة. يجب استيفاء الشرطين.",
  },
  warnBalance:      {
    fr:"⚠ Votre solde est supérieur ou égal à 100 EVP. Le crédit n'est disponible qu'en dessous de 100 EVP.",
    en:"⚠ Your balance is at or above 100 EVP. Credit is only available below 100 EVP.",
    es:"⚠ Tu saldo es igual o superior a 100 EVP. El crédito solo está disponible por debajo de 100 EVP.",
    de:"⚠ Ihr Guthaben beträgt 100 EVP oder mehr. Die Gutschrift ist nur unter 100 EVP verfügbar.",
    pt:"⚠ Seu saldo é igual ou superior a 100 EVP. O crédito só está disponível abaixo de 100 EVP.",
    ar:"⚠ رصيدك يساوي أو يتجاوز 100 EVP. الائتمان متاح فقط أقل من 100 EVP.",
  },
  warnTrades:       {
    fr:"⚠ Clôturez vos positions ouvertes avant de faire une demande de crédit.",
    en:"⚠ Close your open positions before requesting a credit.",
    es:"⚠ Cierra tus posiciones abiertas antes de solicitar un crédito.",
    de:"⚠ Schließen Sie Ihre offenen Positionen, bevor Sie eine Gutschrift beantragen.",
    pt:"⚠ Feche suas posições abertas antes de solicitar um crédito.",
    ar:"⚠ أغلق مراكزك المفتوحة قبل طلب الائتمان.",
  },
  sendingRequest:   { fr:"Envoi de la demande…",   en:"Sending request…",   es:"Enviando solicitud…", de:"Anfrage wird gesendet…", pt:"Enviando solicitação…", ar:"جاري إرسال الطلب…" },
  requestEVP:       {
    fr:"Demander +{amount} EVP",
    en:"Request +{amount} EVP",
    es:"Solicitar +{amount} EVP",
    de:"+{amount} EVP anfordern",
    pt:"Solicitar +{amount} EVP",
    ar:"اطلب +{amount} EVP",
  },
  condsNotMet:      { fr:"Conditions non remplies", en:"Conditions not met", es:"Condiciones no cumplidas", de:"Bedingungen nicht erfüllt", pt:"Condições não atendidas", ar:"الشروط غير مستوفاة" },
  requestErrorFallback: { fr:"Impossible d'envoyer la demande.", en:"Unable to send the request.", es:"No se pudo enviar la solicitud.", de:"Anfrage konnte nicht gesendet werden.", pt:"Não foi possível enviar a solicitação.", ar:"تعذّر إرسال الطلب." },
  boostNote1:       {
    fr:"Le crédit est accordé une seule fois par compte. Il sera crédité sous 24h après validation manuelle.",
    en:"Credit is granted once per account. It will be credited within 24h after manual validation.",
    es:"El crédito se concede una sola vez por cuenta. Se acreditará en 24h tras validación manual.",
    de:"Die Gutschrift wird einmal pro Konto gewährt. Sie wird innerhalb von 24 Stunden nach manueller Prüfung gutgeschrieben.",
    pt:"O crédito é concedido uma vez por conta. Será creditado em 24h após validação manual.",
    ar:"يُمنَح الرصيد مرة واحدة لكل حساب. سيتم إيداعه خلال 24 ساعة بعد التحقق اليدوي.",
  },
  boostNote2:       {
    fr:"Ce crédit est destiné à vous permettre de continuer à trader. Il ne peut pas être retiré directement.",
    en:"This credit is intended to allow you to keep trading. It cannot be withdrawn directly.",
    es:"Este crédito está destinado a permitirte seguir operando. No puede retirarse directamente.",
    de:"Diese Gutschrift soll Ihnen ermöglichen, weiter zu handeln. Sie kann nicht direkt abgehoben werden.",
    pt:"Este crédito destina-se a permitir que você continue a operar. Não pode ser retirado diretamente.",
    ar:"هذا الرصيد مخصص للسماح لك بمواصلة التداول. لا يمكن سحبه مباشرة.",
  },

  /* ─── Bank transfer ───────────────────────────────────────────────── */
  bankAmount:       { fr:"Montant à virer (USD)", en:"Amount to transfer (USD)", es:"Monto a transferir (USD)", de:"Zu überweisender Betrag (USD)", pt:"Valor a transferir (USD)", ar:"المبلغ المُحوَّل (دولار)" },
  bankAmountPh:     { fr:"Montant (min. $100)",   en:"Amount (min. $100)", es:"Monto (mín. $100)", de:"Betrag (mind. $100)", pt:"Valor (mín. $100)", ar:"المبلغ (الحد الأدنى 100$)" },
  bankDetails:      { fr:"Coordonnées bancaires", en:"Bank details",       es:"Datos bancarios",   de:"Bankverbindung",     pt:"Dados bancários",    ar:"التفاصيل المصرفية" },
  bankBeneficiary:  { fr:"Bénéficiaire",          en:"Beneficiary",        es:"Beneficiario",      de:"Begünstigter",       pt:"Beneficiário",       ar:"المستفيد"      },
  bankIBAN:         { fr:"IBAN",                  en:"IBAN",               es:"IBAN",              de:"IBAN",               pt:"IBAN",               ar:"IBAN"          },
  bankBIC:          { fr:"BIC / SWIFT",           en:"BIC / SWIFT",        es:"BIC / SWIFT",       de:"BIC / SWIFT",        pt:"BIC / SWIFT",        ar:"BIC / SWIFT"   },
  bankName:         { fr:"Banque",                en:"Bank",               es:"Banco",             de:"Bank",               pt:"Banco",              ar:"المصرف"        },
  bankCountry:      { fr:"Pays",                  en:"Country",            es:"País",              de:"Land",               pt:"País",               ar:"الدولة"        },
  bankRef:          { fr:"Référence obligatoire", en:"Required reference", es:"Referencia obligatoria", de:"Erforderliche Referenz", pt:"Referência obrigatória", ar:"المرجع الإلزامي" },
  bankRefDesc:      {
    fr:"Indiquez ce code en référence du virement — sans lui votre dépôt ne pourra pas être associé à votre compte.",
    en:"Include this code as the transfer reference — without it your deposit cannot be linked to your account.",
    es:"Incluye este código como referencia de la transferencia — sin él tu depósito no podrá asociarse a tu cuenta.",
    de:"Geben Sie diesen Code als Überweisungsreferenz an — ohne ihn kann Ihre Einzahlung nicht Ihrem Konto zugeordnet werden.",
    pt:"Inclua este código como referência da transferência — sem ele seu depósito não poderá ser associado à sua conta.",
    ar:"أدرج هذا الرمز كمرجع للتحويل — بدونه لن يتمكن إيداعك من الارتباط بحسابك.",
  },
  copyReference:    { fr:"Copier la référence",   en:"Copy reference",     es:"Copiar referencia", de:"Referenz kopieren",  pt:"Copiar referência",  ar:"نسخ المرجع"    },
  creditDelay:      { fr:"Délai de crédit",       en:"Credit delay",       es:"Plazo de acreditación", de:"Bearbeitungszeit", pt:"Prazo de crédito",   ar:"مدة الإيداع"   },
  workingDays:      { fr:"1–2 jours ouvrés",      en:"1–2 business days",  es:"1–2 días hábiles",  de:"1–2 Werktage",       pt:"1–2 dias úteis",     ar:"1–2 يوم عمل"    },
  fees:             { fr:"Frais",                 en:"Fees",               es:"Comisiones",        de:"Gebühren",           pt:"Taxas",              ar:"الرسوم"        },
  minAmount:        { fr:"Montant minimum",       en:"Minimum amount",     es:"Monto mínimo",      de:"Mindestbetrag",      pt:"Valor mínimo",       ar:"الحد الأدنى"   },
  bankConfirm:      { fr:"J'ai effectué le virement", en:"I have made the transfer", es:"He realizado la transferencia", de:"Ich habe die Überweisung getätigt", pt:"Realizei a transferência", ar:"لقد أجريت التحويل" },
  bankNote1: {
    fr:"Le virement doit provenir d'un compte bancaire à votre nom. Les virements de tiers sont refusés.",
    en:"The transfer must come from a bank account in your name. Third-party transfers are refused.",
    es:"La transferencia debe provenir de una cuenta bancaria a tu nombre. Las transferencias de terceros son rechazadas.",
    de:"Die Überweisung muss von einem Bankkonto auf Ihren Namen kommen. Drittüberweisungen werden abgelehnt.",
    pt:"A transferência deve vir de uma conta bancária em seu nome. Transferências de terceiros são recusadas.",
    ar:"يجب أن يأتي التحويل من حساب مصرفي باسمك. التحويلات من أطراف ثالثة مرفوضة.",
  },
  bankNote2: {
    fr:"Toujours inclure la référence {ref} pour que votre dépôt soit traité.",
    en:"Always include the reference {ref} so your deposit can be processed.",
    es:"Incluye siempre la referencia {ref} para que tu depósito sea procesado.",
    de:"Geben Sie immer die Referenz {ref} an, damit Ihre Einzahlung verarbeitet werden kann.",
    pt:"Sempre inclua a referência {ref} para que seu depósito seja processado.",
    ar:"أدرج دائمًا المرجع {ref} حتى يتم معالجة إيداعك.",
  },

  /* ─── Success screen ──────────────────────────────────────────────── */
  successDeposit:   { fr:"Dépôt",                 en:"Deposit",            es:"Depósito",          de:"Einzahlung",         pt:"Depósito",           ar:"إيداع"         },
  successConfirmed: { fr:"Confirmé",              en:"Confirmed",          es:"Confirmado",        de:"Bestätigt",          pt:"Confirmado",         ar:"مُؤكَّد"        },
  successCard:      { fr:"Paiement accepté !",    en:"Payment accepted!",  es:"¡Pago aceptado!",   de:"Zahlung akzeptiert!", pt:"Pagamento aceito!", ar:"تم قبول الدفع!" },
  successMobile:    { fr:"Dépôt Mobile Money initié !", en:"Mobile Money deposit initiated!", es:"¡Depósito Mobile Money iniciado!", de:"Mobile-Money-Einzahlung gestartet!", pt:"Depósito Mobile Money iniciado!", ar:"تم بدء إيداع Mobile Money!" },
  successBoost:     { fr:"Demande de crédit envoyée !", en:"Credit request sent!", es:"¡Solicitud de crédito enviada!", de:"Kreditanfrage gesendet!", pt:"Solicitação de crédito enviada!", ar:"تم إرسال طلب الائتمان!" },
  successBank:      { fr:"Virement enregistré !", en:"Transfer recorded!", es:"¡Transferencia registrada!", de:"Überweisung erfasst!", pt:"Transferência registrada!", ar:"تم تسجيل التحويل!" },
  successCardDesc:  {
    fr:"${amount} USDC seront crédités sur votre wallet dans quelques instants.",
    en:"${amount} USDC will be credited to your wallet shortly.",
    es:"${amount} USDC se acreditarán en tu wallet en unos instantes.",
    de:"${amount} USDC werden in Kürze Ihrer Wallet gutgeschrieben.",
    pt:"${amount} USDC serão creditados na sua carteira em instantes.",
    ar:"سيتم إيداع {amount}$ USDC في محفظتك خلال لحظات.",
  },
  successMobileDesc: {
    fr:"Confirmez la transaction sur votre téléphone. Votre solde sera mis à jour sous quelques minutes.",
    en:"Confirm the transaction on your phone. Your balance will be updated within a few minutes.",
    es:"Confirma la transacción en tu teléfono. Tu saldo se actualizará en unos minutos.",
    de:"Bestätigen Sie die Transaktion auf Ihrem Telefon. Ihr Guthaben wird innerhalb weniger Minuten aktualisiert.",
    pt:"Confirme a transação no seu telefone. Seu saldo será atualizado em alguns minutos.",
    ar:"أكّد المعاملة على هاتفك. سيتم تحديث رصيدك خلال بضع دقائق.",
  },
  successBoostDesc: {
    fr:"Votre demande de crédit de $100 USDC a été enregistrée. Votre compte sera crédité dans les 24 prochaines heures.",
    en:"Your credit request of $100 USDC has been recorded. Your account will be credited within the next 24 hours.",
    es:"Tu solicitud de crédito de $100 USDC ha sido registrada. Tu cuenta será acreditada en las próximas 24 horas.",
    de:"Ihre Kreditanfrage über $100 USDC wurde erfasst. Ihr Konto wird innerhalb der nächsten 24 Stunden gutgeschrieben.",
    pt:"Sua solicitação de crédito de $100 USDC foi registrada. Sua conta será creditada nas próximas 24 horas.",
    ar:"تم تسجيل طلب الائتمان الخاص بك بقيمة 100 دولار USDC. سيتم إيداع المبلغ في حسابك خلال 24 ساعة القادمة.",
  },
  successBankDesc: {
    fr:"Dès réception de votre virement de ${amount}, votre solde sera mis à jour sous 1–2 jours ouvrés.",
    en:"As soon as your ${amount} transfer is received, your balance will be updated within 1–2 business days.",
    es:"En cuanto se reciba tu transferencia de ${amount}, tu saldo se actualizará en 1–2 días hábiles.",
    de:"Sobald Ihre Überweisung von ${amount} eingegangen ist, wird Ihr Guthaben innerhalb von 1–2 Werktagen aktualisiert.",
    pt:"Assim que sua transferência de ${amount} for recebida, seu saldo será atualizado em 1–2 dias úteis.",
    ar:"بمجرد استلام تحويلك بقيمة {amount}$، سيتم تحديث رصيدك خلال 1–2 يوم عمل.",
  },
};

/* Helper standard (key + lang) */
function dp(key, lang) { return DPT[key]?.[lang] ?? DPT[key]?.fr ?? key; }

/* Helper avec interpolation {var} */
function dpv(key, lang, vars = {}) {
  let str = DPT[key]?.[lang] ?? DPT[key]?.fr ?? key;
  for (const k in vars) {
    str = str.replace(new RegExp("\\{" + k + "\\}", "g"), vars[k]);
  }
  return str;
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */
function fmtDateSafe(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("fr-FR", {
    day:    "2-digit",
    month:  "short",
    hour:   "2-digit",
    minute: "2-digit",
  });
}


/* ─── Animations ─────────────────────────────────────────────────────────── */
const fadeIn  = keyframes`from{opacity:0}to{opacity:1}`;
const slideUp = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}`;
const spin    = keyframes`to{transform:rotate(360deg)}`;
const pulse   = keyframes`0%,100%{opacity:1}50%{opacity:.35}`;
const scanline= keyframes`0%{top:-10%}100%{top:110%}`;
const checkPop= keyframes`0%{transform:scale(0)}60%{transform:scale(1.2)}100%{transform:scale(1)}`;

/* ─── Layout ─────────────────────────────────────────────────────────────── */
const Overlay = styled.div`
  position:fixed;inset:0;z-index:300;
  background:rgba(0,0,0,.85);backdrop-filter:blur(10px);
  display:flex;align-items:center;justify-content:center;padding:1rem;
  animation:${fadeIn} .15s ease;
`;
const Modal = styled.div`
  background:${({theme})=>theme.bgCard};
  border:1px solid ${({theme})=>theme.borderLight};
  border-radius:18px;width:100%;max-width:480px;
  max-height:92vh;overflow-y:auto;
  animation:${slideUp} .2s ease;
  scrollbar-width:none;&::-webkit-scrollbar{display:none;}
`;
const Head = styled.div`
  padding:1.25rem 1.5rem;
  border-bottom:1px solid ${({theme})=>theme.border};
  display:flex;align-items:center;justify-content:space-between;
  position:sticky;top:0;background:${({theme})=>theme.bgCard};z-index:2;
`;
const ModalTitle = styled.h2`
  font-family:${({theme})=>theme.fontDisplay};
  font-size:1.15rem;font-weight:700;letter-spacing:-.02em;
  span{color:${({theme})=>theme.green};}
`;
const ModalSub = styled.p`font-size:.72rem;color:${({theme})=>theme.textMuted};margin-top:.2rem;`;
const CloseBtn = styled.button`
  background:none;border:none;color:${({theme})=>theme.textMuted};
  cursor:pointer;font-size:1.1rem;&:hover{color:${({theme})=>theme.text};}
`;
const Body = styled.div`padding:1.25rem 1.5rem;display:flex;flex-direction:column;gap:1.1rem;`;

const MethodTabs = styled.div`
  display:grid;grid-template-columns:repeat(4,1fr);gap:.4rem;
  background:${({theme})=>theme.bg};
  border:1px solid ${({theme})=>theme.border};
  border-radius:10px;padding:.3rem;
`;
const MethodTab = styled.button`
  display:flex;flex-direction:column;align-items:center;gap:.25rem;
  padding:.55rem .4rem;border-radius:7px;border:none;cursor:pointer;
  transition:all .15s;
  background:${({$active,theme})=>$active?theme.bgCard:"transparent"};
  border:1px solid ${({$active,theme})=>$active?theme.borderLight:"transparent"};
  box-shadow:${({$active})=>$active?"0 1px 4px rgba(0,0,0,.3)":"none"};
`;
const TabIcon = styled.span`font-size:1.1rem;line-height:1;`;
const TabLabel = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.58rem;letter-spacing:.04em;
  color:${({$active,theme})=>$active?theme.text:theme.textMuted};
  font-weight:${({$active})=>$active?600:400};
`;

const SectionLabel = styled.div`
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;
  text-transform:uppercase;letter-spacing:.08em;
  color:${({theme})=>theme.textMuted};margin-bottom:.4rem;
`;

const CoinGrid = styled.div`display:grid;grid-template-columns:repeat(4,1fr);gap:.5rem;`;
const CoinBtn = styled.button`
  display:flex;flex-direction:column;align-items:center;gap:.35rem;
  padding:.65rem .4rem;border-radius:10px;
  border:1px solid ${({$active,theme})=>$active?theme.green+"88":theme.border};
  background:${({$active,theme})=>$active?theme.greenDim:"transparent"};
  cursor:pointer;transition:all .15s;
  &:hover{border-color:${({theme})=>theme.borderLight};background:${({theme})=>theme.bgHover};}
`;
const CoinIcon = styled.span`font-size:1.4rem;line-height:1;`;
const CoinName = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.6rem;
  color:${({$active,theme})=>$active?theme.green:theme.textDim};
  font-weight:${({$active})=>$active?600:400};
`;
const CoinPrice = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.58rem;color:${({theme})=>theme.textMuted};`;

const NetRow = styled.div`display:flex;gap:.4rem;flex-wrap:wrap;`;
const NetBadge = styled.button`
  font-family:${({theme})=>theme.fontMono};font-size:.6rem;
  padding:.2rem .6rem;border-radius:4px;
  border:1px solid ${({$active,theme})=>$active?theme.accent+"88":theme.border};
  background:${({$active,theme})=>$active?"rgba(79,195,247,.1)":"transparent"};
  color:${({$active,theme})=>$active?theme.accent:theme.textMuted};
  cursor:pointer;transition:all .15s;letter-spacing:.06em;
  &:hover{border-color:${({theme})=>theme.borderLight};}
`;

const QRBlock = styled.div`
  display:flex;flex-direction:column;align-items:center;gap:1rem;
  background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};
  border-radius:12px;padding:1.25rem;
`;
const QRWrap = styled.div`
  position:relative;width:136px;height:136px;
  background:#fff;border-radius:8px;padding:7px;
  display:flex;align-items:center;justify-content:center;overflow:hidden;
`;
const QRScanLine = styled.div`
  position:absolute;left:0;right:0;height:2px;
  background:linear-gradient(90deg,transparent,${({theme})=>theme.green},transparent);
  animation:${scanline} 2.5s linear infinite;z-index:2;
`;
const QRSvg = styled.svg`width:100%;height:100%;`;
const AddressBox = styled.div`
  width:100%;display:flex;align-items:center;gap:.5rem;
  background:${({theme})=>theme.bgHover};border:1px solid ${({theme})=>theme.border};
  border-radius:8px;padding:.6rem .8rem;
`;
const AddressText = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.7rem;
  color:${({theme})=>theme.textDim};flex:1;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;letter-spacing:.03em;
`;
const CopyBtn = styled.button`
  background:none;border:1px solid ${({theme})=>theme.borderLight};
  border-radius:5px;color:${({theme})=>theme.textDim};
  font-size:.65rem;padding:.25rem .55rem;cursor:pointer;
  font-family:${({theme})=>theme.fontMono};white-space:nowrap;
  transition:all .15s;flex-shrink:0;
  &:hover{color:${({theme})=>theme.green};border-color:${({theme})=>theme.green};}
`;
const MetaRow = styled.div`
  display:flex;justify-content:space-between;width:100%;
  font-family:${({theme})=>theme.fontMono};font-size:.7rem;
`;
const MetaKey = styled.span`color:${({theme})=>theme.textMuted};`;
const MetaVal = styled.span`color:${({theme})=>theme.text};`;
const Spinner = styled.div`
  width:32px;height:32px;border:2px solid ${({theme})=>theme.border};
  border-top-color:${({theme})=>theme.green};border-radius:50%;
  animation:${spin} .8s linear infinite;margin:1rem auto;
`;
const LoadingLabel = styled.p`
  font-family:${({theme})=>theme.fontMono};font-size:.72rem;
  color:${({theme})=>theme.textMuted};text-align:center;
  animation:${pulse} 1.5s ease infinite;
`;

const CardBadge = styled.div`
  padding:.22rem .65rem;border-radius:5px;
  font-family:${({theme})=>theme.fontMono};font-size:.6rem;letter-spacing:.05em;
  border:1px solid ${({theme})=>theme.border};color:${({theme})=>theme.textMuted};
  background:${({theme})=>theme.bg};
`;
const FieldWrap = styled.div`display:flex;flex-direction:column;gap:.28rem;`;
const FieldLabel = styled.label`
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;
  text-transform:uppercase;letter-spacing:.07em;color:${({theme})=>theme.textMuted};
`;
const FieldInput = styled.input`
  background:${({theme})=>theme.bg};
  border:1px solid ${({$err,theme})=>$err?theme.red+"88":theme.border};
  border-radius:7px;color:${({theme})=>theme.text};
  font-family:${({theme})=>theme.fontMono};font-size:.82rem;
  padding:.55rem .75rem;outline:none;transition:border-color .15s;
  &:focus{border-color:${({theme})=>theme.borderLight};}
  &::placeholder{color:${({theme})=>theme.textMuted};font-family:${({theme})=>theme.fontBody};}
`;
const AmountPresets = styled.div`display:flex;gap:.4rem;flex-wrap:wrap;`;
const AmountChip = styled.button`
  padding:.3rem .7rem;border-radius:6px;
  font-family:${({theme})=>theme.fontMono};font-size:.68rem;cursor:pointer;
  transition:all .15s;
  border:1px solid ${({$active,theme})=>$active?theme.green+"88":theme.border};
  background:${({$active,theme})=>$active?theme.greenDim:"transparent"};
  color:${({$active,theme})=>$active?theme.green:theme.textMuted};
  &:hover{border-color:${({theme})=>theme.borderLight};color:${({theme})=>theme.text};}
`;
const FeeRow = styled.div`
  display:flex;justify-content:space-between;align-items:center;
  background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};
  border-radius:8px;padding:.6rem .85rem;
`;
const FeeLabel = styled.span`font-size:.72rem;color:${({theme})=>theme.textMuted};`;
const FeeVal = styled.span`font-family:${({theme})=>theme.fontMono};font-size:.72rem;color:${({theme})=>theme.text};`;
const PayBtn = styled.button`
  width:100%;padding:.8rem;border-radius:9px;border:none;
  background:${({disabled,theme})=>disabled?theme.bgHover:theme.green};
  color:${({disabled})=>disabled?"#5a6a7a":"#000"};
  font-family:${({theme})=>theme.fontBody};font-size:.85rem;font-weight:700;
  cursor:${({disabled})=>disabled?"not-allowed":"pointer"};
  transition:opacity .15s;letter-spacing:.02em;
  &:hover:not(:disabled){opacity:.9;}
`;

const BankCard = styled.div`
  background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};
  border-radius:12px;padding:1rem 1.25rem;display:flex;flex-direction:column;gap:.65rem;
`;
const BankRow = styled.div`
  display:flex;justify-content:space-between;align-items:center;
  padding:.5rem 0;border-bottom:1px solid ${({theme})=>theme.border};
  &:last-child{border-bottom:none;padding-bottom:0;}
`;
const BankKey = styled.span`font-size:.75rem;color:${({theme})=>theme.textMuted};`;
const BankVal = styled.div`
  display:flex;align-items:center;gap:.4rem;
  font-family:${({theme})=>theme.fontMono};font-size:.75rem;color:${({theme})=>theme.text};
`;
const SmallCopy = styled.button`
  background:none;border:1px solid ${({theme})=>theme.border};
  border-radius:4px;color:${({theme})=>theme.textMuted};
  font-size:.58rem;padding:.15rem .4rem;cursor:pointer;
  font-family:${({theme})=>theme.fontMono};transition:all .15s;
  &:hover{color:${({theme})=>theme.green};border-color:${({theme})=>theme.green}55;}
`;
const RefBadge = styled.div`
  background:${({theme})=>theme.greenDim};border:1px solid ${({theme})=>theme.green}44;
  border-radius:8px;padding:.65rem 1rem;text-align:center;
`;
const RefCode = styled.div`
  font-family:${({theme})=>theme.fontMono};font-size:1rem;
  font-weight:600;letter-spacing:.12em;color:${({theme})=>theme.green};
`;
const RefSub = styled.div`font-size:.68rem;color:${({theme})=>theme.textMuted};margin-top:.2rem;`;

const SuccessWrap = styled.div`
  display:flex;flex-direction:column;align-items:center;
  gap:1rem;padding:1.5rem;text-align:center;
`;
const CheckCircle = styled.div`
  width:56px;height:56px;border-radius:50%;
  background:${({theme})=>theme.greenDim};border:1px solid ${({theme})=>theme.green}55;
  display:flex;align-items:center;justify-content:center;
  font-size:1.6rem;animation:${checkPop} .4s ease both;
`;
const SuccessTitle = styled.div`
  font-family:${({theme})=>theme.fontDisplay};font-size:1.1rem;font-weight:700;
  color:${({theme})=>theme.green};
`;
const SuccessSub = styled.div`font-size:.78rem;color:${({theme})=>theme.textMuted};line-height:1.5;max-width:280px;`;

const Notices = styled.div`display:flex;flex-direction:column;gap:.4rem;`;
const Notice = styled.div`
  display:flex;gap:.5rem;align-items:flex-start;
  font-size:.72rem;color:${({theme})=>theme.textMuted};line-height:1.45;
  span:first-child{flex-shrink:0;margin-top:.05rem;}
`;

const COINS = [
  { id:"USDC", icon:"💵", label:"USDC", networks:["Ethereum","Polygon","Arbitrum"], minDeposit:"10",   confirmations:12 },
  { id:"ETH",  icon:"🔷", label:"ETH",  networks:["Ethereum","Arbitrum"],           minDeposit:"0.005",confirmations:12 },
  { id:"BTC",  icon:"₿",  label:"BTC",  networks:["Bitcoin"],                       minDeposit:"0.0001",confirmations:3 },
  { id:"USDT", icon:"💲", label:"USDT", networks:["Ethereum","Tron","Polygon"],     minDeposit:"10",   confirmations:12 },
];
const MOCK_ADDRESSES = {
  USDC_Ethereum:"0x3f5CE5FBFe3E9af3971dD833D26bA9b5C936f0bE",
  USDC_Polygon:"0x742d35Cc6634C0532925a3b8D4C9E62B45e62a72",
  USDC_Arbitrum:"0x1a2B3c4D5e6F7a8B9c0D1e2F3a4B5c6D7e8F9a0B",
  ETH_Ethereum:"0xd8dA6BF26964aF9D7eEd9e03E53415D37aA96045",
  ETH_Arbitrum:"0xAbCd1234EfGh5678IjKl9012MnOp3456QrSt7890",
  BTC_Bitcoin:"bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh",
  USDT_Ethereum:"0x5754284f345afc66a98fbB0a0Afe71e0F007B949",
  USDT_Tron:"TQn9Y2khEsLJW1ChVWFMSMeRDow5KcbLSE",
  USDT_Polygon:"0x9bF4001d307dFd62B26A2F1307ee0C0307632d59",
};
const AMOUNT_PRESETS = [20, 50, 100, 250, 500];
const MOBILE_PRESETS = [5, 10, 25, 50, 100];

function makeQRModules(seed) {
  const size=21; const grid=[]; let h=0;
  for(let i=0;i<seed.length;i++) h=(Math.imul(31,h)+seed.charCodeAt(i))|0;
  const rand=()=>{h^=h<<13;h^=h>>17;h^=h<<5;return((h>>>0)/4294967296);};
  for(let r=0;r<size;r++){
    grid[r]=[];
    for(let c=0;c<size;c++){
      const inFinder=(r<7&&c<7)||(r<7&&c>13)||(r>13&&c<7);
      grid[r][c]=inFinder?((r===0||r===6||c===0||c===6)||(r>1&&r<5&&c>1&&c<5)):rand()>.5;
    }
  }
  return grid;
}
function QRCode({address}){
  const modules=makeQRModules(address||"default"); const size=modules.length; const cell=124/size;
  return(<QRSvg viewBox="0 0 124 124" xmlns="http://www.w3.org/2000/svg">{modules.map((row,r)=>row.map((on,c)=>on?<rect key={`${r}-${c}`} x={c*cell} y={r*cell} width={cell} height={cell} fill="#000"/>:null))}</QRSvg>);
}

function genRef(){ return "PX-"+(Math.random()*1e9|0).toString(36).toUpperCase().slice(0,8); }

const PaysScroll = styled.div`display:flex;gap:.35rem;overflow-x:auto;padding-bottom:.2rem;scrollbar-width:none;&::-webkit-scrollbar{display:none;}`;
const PaysBadge = styled.button`
  flex-shrink:0;padding:.25rem .7rem;border-radius:5px;cursor:pointer;
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;letter-spacing:.04em;
  white-space:nowrap;transition:all .15s;
  border:1px solid ${({$active,theme})=>$active?theme.green+"88":theme.border};
  background:${({$active,theme})=>$active?theme.greenDim:"transparent"};
  color:${({$active,theme})=>$active?theme.green:theme.textMuted};
`;
const PhoneRow = styled.div`display:flex;gap:.5rem;align-items:stretch;`;
const DialCode = styled.div`
  display:flex;align-items:center;gap:.3rem;
  padding:.55rem .75rem;border-radius:7px;
  border:1px solid ${({theme})=>theme.border};background:${({theme})=>theme.bg};
  font-family:${({theme})=>theme.fontMono};font-size:.8rem;
  color:${({theme})=>theme.textDim};white-space:nowrap;flex-shrink:0;
`;
const OtpWrap = styled.div`
  background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};
  border-radius:12px;padding:1.25rem;display:flex;flex-direction:column;
  align-items:center;gap:.85rem;
`;
const OtpTitle = styled.div`
  font-family:${({theme})=>theme.fontMono};font-size:.75rem;
  color:${({theme})=>theme.textMuted};text-align:center;line-height:1.5;
`;

const MomoGrid  = styled.div`display:grid;grid-template-columns:repeat(2,1fr);gap:.5rem;`;
const MomoBtn   = styled.button`
  display:flex;align-items:center;gap:.65rem;
  padding:.7rem .85rem;border-radius:10px;cursor:pointer;transition:all .15s;
  border:1px solid ${({$active,theme})=>$active?`${theme.green}88`:theme.border};
  background:${({$active,theme})=>$active?theme.greenDim:"transparent"};
  &:hover{border-color:${({theme})=>theme.borderLight};background:${({theme})=>theme.bgHover};}
`;
const MomoLogo  = styled.div`
  width:34px;height:34px;border-radius:8px;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;
  font-size:.85rem;font-weight:800;
  background:${({$bg})=>$bg};color:${({$col})=>$col||"#fff"};
  font-family:'DM Mono','Courier New',monospace;letter-spacing:-.04em;
`;
const MomoName  = styled.div`display:flex;flex-direction:column;gap:.05rem;text-align:left;`;
const MomoTitle = styled.span`
  font-family:${({theme})=>theme.fontMono};font-size:.72rem;font-weight:600;
  color:${({$active,theme})=>$active?theme.green:theme.text};
`;
const MomoPays  = styled.span`font-size:.62rem;color:${({theme})=>theme.textMuted};`;

const TINGG_OPERATORS = [
  { id:"mpesa",   name:"M-Pesa",       bg:"#4CAF50", col:"#fff", codes:["KE","TZ","MZ","CD"] },
  { id:"orange",  name:"Orange Money", bg:"#FF6600", col:"#fff", codes:["CI","SN","CM","BF","ML","CD"] },
  { id:"mtn",     name:"MTN MoMo",     bg:"#FFC107", col:"#000", codes:["GH","NG","CM","CI","UG","ZM","RW","BJ"] },
  { id:"wave",    name:"Wave",         bg:"#00BCD4", col:"#fff", codes:["CI","SN","ML","BF"] },
  { id:"airtel",  name:"Airtel Money", bg:"#E53935", col:"#fff", codes:["KE","TZ","UG","ZM","MW","NG","CD"] },
  { id:"moov",    name:"Moov Money",   bg:"#1565C0", col:"#fff", codes:["CI","BJ","BF","ML","TG"] },
  { id:"telebirr",name:"Telebirr",     bg:"#4CAF50", col:"#fff", codes:["ET"] },
  { id:"ecocash", name:"EcoCash",      bg:"#E53935", col:"#fff", codes:["ZW"] },
  { id:"tmoney",  name:"T-Money",      bg:"#7B1FA2", col:"#fff", codes:["TG"] },
  { id:"flooz",   name:"Flooz",        bg:"#F57C00", col:"#fff", codes:["TG","BJ"] },
  { id:"zamtel",  name:"Zamtel",       bg:"#2E7D32", col:"#fff", codes:["ZM"] },
  { id:"tigo",    name:"Tigo Pesa",    bg:"#1976D2", col:"#fff", codes:["TZ"] },
];

const TINGG_COUNTRIES = [
  { code:"CI", flag:"🇨🇮", name:"Côte d'Ivoire", currency:"XOF", operators:["Orange Money","MTN MoMo","Moov","Wave"] },
  { code:"SN", flag:"🇸🇳", name:"Sénégal",       currency:"XOF", operators:["Orange Money","Free Money","Wave"] },
  { code:"CM", flag:"🇨🇲", name:"Cameroun",      currency:"XAF", operators:["MTN MoMo","Orange Money"] },
  { code:"GH", flag:"🇬🇭", name:"Ghana",         currency:"GHS", operators:["MTN MoMo","Vodafone Cash","AirtelTigo"] },
  { code:"KE", flag:"🇰🇪", name:"Kenya",         currency:"KES", operators:["M-Pesa","Airtel Money"] },
  { code:"TZ", flag:"🇹🇿", name:"Tanzanie",      currency:"TZS", operators:["M-Pesa","Tigo Pesa","Airtel Money"] },
  { code:"UG", flag:"🇺🇬", name:"Uganda",        currency:"UGX", operators:["MTN MoMo","Airtel Money"] },
  { code:"NG", flag:"🇳🇬", name:"Nigeria",       currency:"NGN", operators:["MTN MoMo","Airtel Money"] },
  { code:"ZM", flag:"🇿🇲", name:"Zambie",        currency:"ZMW", operators:["MTN MoMo","Airtel Money","Zamtel"] },
  { code:"MW", flag:"🇲🇼", name:"Malawi",        currency:"MWK", operators:["Airtel Money","TNM Mpamba"] },
  { code:"RW", flag:"🇷🇼", name:"Rwanda",        currency:"RWF", operators:["MTN MoMo","Airtel Money"] },
  { code:"ET", flag:"🇪🇹", name:"Éthiopie",      currency:"ETB", operators:["Telebirr"] },
  { code:"BF", flag:"🇧🇫", name:"Burkina Faso",  currency:"XOF", operators:["Orange Money","Moov Money"] },
  { code:"ML", flag:"🇲🇱", name:"Mali",          currency:"XOF", operators:["Orange Money","Moov Money"] },
  { code:"BJ", flag:"🇧🇯", name:"Bénin",         currency:"XOF", operators:["MTN MoMo","Moov Money"] },
  { code:"TG", flag:"🇹🇬", name:"Togo",          currency:"XOF", operators:["T-Money","Flooz"] },
  { code:"MZ", flag:"🇲🇿", name:"Mozambique",    currency:"MZN", operators:["M-Pesa","Airtel Money"] },
  { code:"CD", flag:"🇨🇩", name:"Congo RD",      currency:"CDF", operators:["M-Pesa","Airtel Money","Orange Money"] },
  { code:"ZW", flag:"🇿🇼", name:"Zimbabwe",      currency:"USD", operators:["EcoCash","OneMoney"] },
  { code:"ZA", flag:"🇿🇦", name:"Afrique du Sud",currency:"ZAR", operators:["MTN MoMo"] },
];
const DIAL_CODES = {
  CI:"+225",SN:"+221",CM:"+237",GH:"+233",KE:"+254",TZ:"+255",
  UG:"+256",NG:"+234",ZM:"+260",MW:"+265",RW:"+250",ET:"+251",
  BF:"+226",ML:"+223",BJ:"+229",TG:"+228",MZ:"+258",CD:"+243",ZW:"+263",ZA:"+27",
};

const StripePowered = styled.div`
  display:flex;align-items:center;gap:.4rem;
  font-family:${({theme})=>theme.fontMono};font-size:.6rem;color:${({theme})=>theme.textMuted};
`;

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLIC_KEY || "";
const API_BASE  = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

let stripePromise = null;
function getStripe() {
  if (!stripePromise) {
    stripePromise = new Promise((resolve, reject) => {
      if (window.Stripe) { resolve(window.Stripe(STRIPE_PK)); return; }
      const s = document.createElement("script");
      s.src = "https://js.stripe.com/v3/";
      s.onload  = () => resolve(window.Stripe(STRIPE_PK));
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return stripePromise;
}

/* ─── StripeCardSection ──────────────────────────────────────────────── */
function StripeCardSection({ amt, setAmt, onSuccess, lang }) {
  const stripeRef = useRef(null);
  const [cardNum,  setCardNum]  = useState("");
  const [expiry,   setExpiry]   = useState("");
  const [cvc,      setCvc]      = useState("");
  const [name,     setName]     = useState("");
  const [paying,   setPaying]   = useState(false);
  const [error,    setError]    = useState("");
  const [noKey,    setNoKey]    = useState(!STRIPE_PK);

  const stripeFee    = (parseFloat(amt) || 0) * 0.029 + 0.30;
  const totalDisplay = ((parseFloat(amt) || 0) + stripeFee).toFixed(2);

  const fmtCard   = v => v.replace(/\D/g,"").slice(0,16).replace(/(\d{4})(?=\d)/g,"$1 ");
  const fmtExpiry = v => v.replace(/\D/g,"").slice(0,4).replace(/(\d{2})(\d)/,"$1/$2");
  const fmtCvc    = v => v.replace(/\D/g,"").slice(0,4);

  const rawCard = cardNum.replace(/\s/g,"");
  const isComplete = rawCard.length === 16 && expiry.length >= 5 && cvc.length >= 3 && name.trim() && parseFloat(amt) >= 5;

  useEffect(() => {
    if (!STRIPE_PK) return;
    getStripe().then(s => { stripeRef.current = s; }).catch(() => {});
  }, []);

  const handlePay = async () => {
    if (!isComplete || paying) return;
    if (!STRIPE_PK) { setNoKey(true); return; }
    setPaying(true); setError("");

    try {
      const stripe = stripeRef.current || await getStripe();
      const [expM, expY] = expiry.split("/");

      const { error: pmErr, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: { number: rawCard, exp_month: parseInt(expM,10), exp_year: parseInt("20"+expY,10), cvc },
        billing_details: { name: name.trim() },
      });
      if (pmErr) throw new Error(pmErr.message);

      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API_BASE}/account/stripe/payment-intent/`, {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},
        body: JSON.stringify({ amount: parseFloat(amt) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || dp("serverError", lang));

      const { error: cfErr, paymentIntent } = await stripe.confirmCardPayment(
        data.client_secret, { payment_method: paymentMethod.id }
      );
      if (cfErr) throw new Error(cfErr.message);
      if (paymentIntent.status === "succeeded") {
        try {
          await fetch(`${API_BASE}/account/stripe/confirm/`, {
            method:"POST",
            headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},
            body: JSON.stringify({ payment_intent_id: paymentIntent.id }),
          });
        } catch(_) {}
        onSuccess();
      } else throw new Error(`Status: ${paymentIntent.status}`);
    } catch (e) {
      setError(e.message || dp("paymentFailed", lang));
    } finally { setPaying(false); }
  };

  if (noKey) return (
    <div style={{background:"rgba(255,193,7,.06)",border:"1px solid rgba(255,193,7,.25)",
      borderRadius:"10px",padding:"1.25rem",display:"flex",flexDirection:"column",gap:".75rem"}}>
      <div style={{fontSize:".85rem",fontWeight:600,color:"#ffc107"}}>⚙ {dp("stripeNotConfig", lang)}</div>
      <div style={{fontSize:".75rem",color:"#8a9aaa",lineHeight:1.6}}>{dp("stripeNotConfigDesc", lang)}</div>
      <pre style={{background:"#080a0c",border:"1px solid #1e2530",borderRadius:"7px",
        padding:".75rem",fontSize:".72rem",color:"#00e676",margin:0,overflowX:"auto"}}>
{`REACT_APP_STRIPE_PUBLIC_KEY=pk_test_...`}
      </pre>
      <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer"
        style={{fontSize:".72rem",color:"#4fc3f7"}}>→ dashboard.stripe.com/apikeys</a>
    </div>
  );

  return (<>
    <div>
      <SectionLabel>{dp("amountUSD", lang)}</SectionLabel>
      <AmountPresets>
        {AMOUNT_PRESETS.map(v=>(
          <AmountChip key={v} $active={amt===String(v)} onClick={()=>setAmt(String(v))}>${v}</AmountChip>
        ))}
      </AmountPresets>
      <FieldInput style={{marginTop:".5rem"}} type="number" min="5"
        placeholder={dp("amountCustom", lang)} value={amt} onChange={e=>setAmt(e.target.value)}/>
    </div>

    <div style={{display:"flex",flexDirection:"column",gap:".6rem"}}>
      <SectionLabel>{dp("cardInfo", lang)}</SectionLabel>

      <FieldWrap>
        <FieldLabel>{dp("cardNumber", lang)}</FieldLabel>
        <FieldInput placeholder="4242 4242 4242 4242" value={cardNum}
          onChange={e=>setCardNum(fmtCard(e.target.value))} inputMode="numeric" maxLength={19}/>
      </FieldWrap>

      <FieldWrap>
        <FieldLabel>{dp("cardName", lang)}</FieldLabel>
        <FieldInput placeholder={dp("cardNamePh", lang)} value={name}
          onChange={e=>setName(e.target.value.toUpperCase())}/>
      </FieldWrap>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:".6rem"}}>
        <FieldWrap>
          <FieldLabel>{dp("cardExpiry", lang)}</FieldLabel>
          <FieldInput placeholder={dp("cardExpiryPh", lang)} value={expiry}
            onChange={e=>setExpiry(fmtExpiry(e.target.value))} inputMode="numeric" maxLength={5}/>
        </FieldWrap>
        <FieldWrap>
          <FieldLabel>{dp("cvc", lang)}</FieldLabel>
          <FieldInput placeholder="123" value={cvc}
            onChange={e=>setCvc(fmtCvc(e.target.value))} inputMode="numeric" maxLength={4} type="password"/>
        </FieldWrap>
      </div>

      <div style={{display:"flex",gap:".3rem",flexWrap:"wrap",alignItems:"center"}}>
        {["VISA","Mastercard","Amex","CB"].map(b=>(
          <CardBadge key={b} style={{padding:".14rem .5rem",fontSize:".58rem"}}>{b}</CardBadge>
        ))}
        <span style={{fontSize:".62rem",color:"#5a6a7a",marginLeft:"auto",fontFamily:"DM Mono,monospace"}}>
          🔒 Stripe PCI DSS
        </span>
      </div>
    </div>

    <div style={{background:"rgba(79,195,247,.05)",border:"1px solid rgba(79,195,247,.15)",
      borderRadius:"8px",padding:".65rem .85rem"}}>
      <div style={{fontSize:".62rem",color:"#4fc3f7",fontWeight:600,marginBottom:".35rem",
        fontFamily:"DM Mono,monospace",textTransform:"uppercase",letterSpacing:".06em"}}>
        {dp("stripeTestCards", lang)}
      </div>
      {[
        {num:"4242 4242 4242 4242", label:dp("stripeAccepted", lang)},
        {num:"4000 0000 0000 0002", label:dp("stripeRefused",  lang)},
        {num:"4000 0025 0000 3155", label:dp("stripe3ds",      lang)},
      ].map(c=>(
        <div key={c.num} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
          padding:".2rem 0",fontSize:".7rem"}}>
          <span style={{fontFamily:"DM Mono,monospace",color:"#e8edf2",letterSpacing:".04em",cursor:"pointer"}}
            onClick={()=>setCardNum(c.num)}>{c.num}</span>
          <span style={{color:"#5a6a7a",fontSize:".65rem"}}>{c.label}</span>
        </div>
      ))}
      <div style={{fontSize:".65rem",color:"#5a6a7a",marginTop:".3rem"}}>
        {dp("stripeTestHint", lang)}
      </div>
    </div>

    {parseFloat(amt) >= 5 && (
      <div style={{display:"flex",flexDirection:"column",gap:".35rem"}}>
        <FeeRow><FeeLabel>{dp("stripeFee", lang)}</FeeLabel><FeeVal>+${stripeFee.toFixed(2)}</FeeVal></FeeRow>
        <FeeRow>
          <FeeLabel style={{fontWeight:600,color:"#e8edf2"}}>{dp("stripeTotalCharged", lang)}</FeeLabel>
          <FeeVal style={{color:"#00e676",fontWeight:600}}>${totalDisplay}</FeeVal>
        </FeeRow>
        <FeeRow><FeeLabel>{dp("stripeWalletCredit", lang)}</FeeLabel><FeeVal>${parseFloat(amt).toFixed(2)} USDC</FeeVal></FeeRow>
      </div>
    )}

    {error && (
      <div style={{fontSize:".75rem",color:"#ff3b5c",background:"#ff3b5c11",
        border:"1px solid #ff3b5c33",borderRadius:"7px",padding:".6rem .8rem"}}>✕ {error}</div>
    )}

    <PayBtn disabled={!isComplete || paying} onClick={handlePay}>
      {paying ? dp("processing", lang) : dpv("payBtn", lang, {amount: totalDisplay})}
    </PayBtn>

    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
      <StripePowered>
        <svg width="40" height="16" viewBox="0 0 60 25" fill="none">
          <path d="M5.5 10c0-1.7 1.4-2.4 3.6-2.4 3.2 0 7.2 1 10.4 2.8V3.6C16.3 1.7 12.8 1 9.1 1 3.8 1 0 3.8 0 10.3c0 10 13.8 8.4 13.8 12.7 0 2-1.7 2.6-4.1 2.6-3.5 0-8-1.4-11.5-3.4v6.9c3.9 1.7 7.9 2.4 11.5 2.4 8.7 0 14.7-4.3 14.7-10.9C24.4 11.2 5.5 13.1 5.5 10z" fill="#635BFF"/>
        </svg>
        <span>{dp("stripeSecured", lang)}</span>
      </StripePowered>
    </div>

    <Notices>
      <Notice><span>🔒</span><span>{dp("stripeNote1", lang)}</span></Notice>
      <Notice><span>🛡</span><span>{dp("stripeNote2", lang)}</span></Notice>
    </Notices>
  </>);
}

/* ─── MobileMoney — Tingg ──────────────────────────────────────────── */
function MobileMoney({ pays, setPays, amt, setAmt, onSuccess, lang }) {
  const [phone,    setPhone]    = useState("");
  const [selOp,    setSelOp]    = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");
  const [step,     setStep]     = useState("form");

  const country  = TINGG_COUNTRIES.find(c => c.code === pays);
  const dialCode = DIAL_CODES[pays] || "+XX";

  const handleSelectOp = (op) => {
    setSelOp(op);
    if (!pays && op.codes.length > 0) setPays(op.codes[0]);
  };

  const visibleOps = pays
    ? TINGG_OPERATORS.filter(o => o.codes.includes(pays))
    : TINGG_OPERATORS;

  const canPay = pays && phone.length >= 6 && parseFloat(amt) >= 5;

  const handlePay = async () => {
    if (!canPay) return;
    setLoading(true); setError("");
    try {
      const token = localStorage.getItem("access_token");
      const res = await fetch(`${API_BASE}/account/tingg/checkout/`, {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":`Bearer ${token}`},
        body:JSON.stringify({
          amount:       parseFloat(amt),
          country_code: pays,
          phone:        dialCode.replace("+","") + phone.replace(/^0/,""),
          currency:     country?.currency || "USD",
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || dp("serverError", lang));
      if (data.checkout_url) {
        const popup = window.open(data.checkout_url,"tingg_checkout","width=480,height=640,scrollbars=yes");
        if (!popup) window.location.href = data.checkout_url;
      }
      setStep("pending");
    } catch(e) {
      setError(e.message || dp("tinggInitFail", lang));
    } finally { setLoading(false); }
  };

  if (step === "pending") return (
    <OtpWrap>
      <div style={{fontSize:"2rem"}}>📲</div>
      <OtpTitle>
        <strong style={{color:"#e8edf2",display:"block",marginBottom:".4rem",fontSize:".85rem"}}>
          {dp("tinggCheckPhone", lang)}
        </strong>
        {dp("tinggNotifSent", lang)}<br/>
        <strong style={{color:"#e8edf2"}}>{dialCode} {phone}</strong>.<br/><br/>
        {dp("tinggAccept", lang)}
      </OtpTitle>
      <div style={{width:"100%",display:"flex",flexDirection:"column",gap:".4rem"}}>
        {[
          {n:1, label:dp("tinggStep1", lang), done:true},
          {n:2, label:dp("tinggStep2", lang), done:false},
          {n:3, label:dp("tinggStep3", lang), done:false},
        ].map(s=>(
          <div key={s.n} style={{display:"flex",alignItems:"center",gap:".6rem",
            padding:".45rem .65rem",borderRadius:"7px",
            background:s.done?"rgba(0,230,118,.06)":"transparent",
            border:`1px solid ${s.done?"rgba(0,230,118,.2)":"#1e2530"}`}}>
            <span style={{width:20,height:20,borderRadius:"50%",flexShrink:0,
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:".62rem",fontWeight:700,fontFamily:"DM Mono,monospace",
              background:s.done?"#00e676":"#1e2530",color:s.done?"#000":"#5a6a7a"}}>
              {s.done?"✓":s.n}
            </span>
            <span style={{fontSize:".72rem",color:s.done?"#8a9aaa":"#5a6a7a"}}>{s.label}</span>
          </div>
        ))}
      </div>
      <PayBtn onClick={onSuccess} style={{background:"#00e676",color:"#000"}}>
        {dp("tinggConfirmed", lang)}
      </PayBtn>
      <span style={{fontSize:".7rem",color:"#5a6a7a",cursor:"pointer",textDecoration:"underline"}}
        onClick={()=>setStep("form")}>{dp("tinggRestart", lang)}</span>
    </OtpWrap>
  );

  return (<>
    <div>
      <SectionLabel>
        {dp("country", lang)}
        {pays && country && (
          <span style={{marginLeft:".5rem",color:"#4fc3f7",fontWeight:400,
            textTransform:"none",letterSpacing:0}}>
            — {country.operators.length} {country.operators.length > 1 ? dp("operators", lang) : dp("operator", lang)}
          </span>
        )}
      </SectionLabel>
      <PaysScroll>
        <PaysBadge $active={!pays} onClick={()=>{setPays(null);setPhone("");setSelOp(null);}}>{dp("countryAll", lang)}</PaysBadge>
        {TINGG_COUNTRIES.map(c=>(
          <PaysBadge key={c.code} $active={pays===c.code}
            onClick={()=>{setPays(c.code);setPhone("");setSelOp(null);}}>
            {c.flag} {c.name}
            <span style={{marginLeft:".3rem",borderRadius:"100px",padding:"0 .3rem",fontSize:".55rem",
              background:pays===c.code?"rgba(0,230,118,.2)":"rgba(255,255,255,.08)",
              color:pays===c.code?"#00e676":"#5a6a7a"}}>
              {c.operators.length}
            </span>
          </PaysBadge>
        ))}
      </PaysScroll>
    </div>

    <div>
      <SectionLabel>
        {pays ? dp("operatorsAvailable", lang) : dp("operatorMobile", lang)}
        {pays && country && (
          <span style={{marginLeft:".5rem",color:"#4fc3f7",fontWeight:400,
            textTransform:"none",letterSpacing:0}}>
            — {visibleOps.length} {visibleOps.length > 1 ? dp("operators", lang) : dp("operator", lang)}
          </span>
        )}
      </SectionLabel>

      {!pays ? (
        <MomoGrid>
          {visibleOps.map(op=>(
            <MomoBtn key={op.id} $active={selOp?.id===op.id} onClick={()=>handleSelectOp(op)}>
              <MomoLogo $bg={op.bg} $col={op.col}>{op.name.slice(0,2)}</MomoLogo>
              <MomoName>
                <MomoTitle $active={selOp?.id===op.id}>{op.name}</MomoTitle>
                <MomoPays>
                  {op.codes.slice(0,3).map(c=>{
                    const ctry = TINGG_COUNTRIES.find(t=>t.code===c);
                    return ctry ? ctry.flag : c;
                  }).join(" ")}
                  {op.codes.length > 3 && ` +${op.codes.length-3}`}
                </MomoPays>
              </MomoName>
            </MomoBtn>
          ))}
        </MomoGrid>
      ) : (
        <div style={{display:"flex",flexWrap:"wrap",gap:".35rem"}}>
          {country?.operators.map(name=>{
            const op = TINGG_OPERATORS.find(o=>o.name===name||name.includes(o.name.split(" ")[0]));
            return (
              <div key={name} style={{display:"flex",alignItems:"center",gap:".5rem",
                padding:".4rem .8rem",borderRadius:"8px",
                border:"1px solid #1e2530",background:"#080a0c"}}>
                {op && (
                  <div style={{width:20,height:20,borderRadius:"5px",flexShrink:0,
                    background:op.bg,display:"flex",alignItems:"center",justifyContent:"center",
                    fontSize:".55rem",fontWeight:800,color:op.col||"#fff",
                    fontFamily:"DM Mono,monospace"}}>{op.name.slice(0,2)}</div>
                )}
                <span style={{fontSize:".72rem",color:"#8a9aaa"}}>{name}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>

    <div>
      <SectionLabel>{dp("amountUSD", lang)}</SectionLabel>
      <AmountPresets>
        {MOBILE_PRESETS.map(v=>(
          <AmountChip key={v} $active={amt===String(v)} onClick={()=>setAmt(String(v))}>${v}</AmountChip>
        ))}
      </AmountPresets>
      <FieldInput style={{marginTop:".5rem"}} type="number" min="5"
        placeholder={dp("amountCustom", lang)} value={amt} onChange={e=>setAmt(e.target.value)}/>
      {country && parseFloat(amt)>0 && (
        <div style={{fontSize:".68rem",color:"#5a6a7a",marginTop:".35rem",fontFamily:"DM Mono,monospace"}}>
          {dpv("tinggConvert", lang, {amount: parseFloat(amt).toFixed(2), currency: country.currency})}
        </div>
      )}
    </div>

    <FieldWrap>
      <FieldLabel>{dp("phoneNumber", lang)}</FieldLabel>
      <PhoneRow>
        <DialCode>{dialCode}</DialCode>
        <FieldInput style={{flex:1}} type="tel" placeholder="6X XX XX XX XX"
          value={phone} onChange={e=>setPhone(e.target.value.replace(/\D/g,"").slice(0,12))}/>
      </PhoneRow>
    </FieldWrap>

    {pays && parseFloat(amt)>=5 && (
      <div style={{display:"flex",flexDirection:"column",gap:".35rem"}}>
        <FeeRow><FeeLabel>{dp("feeTingg", lang)}</FeeLabel><FeeVal style={{color:"#00e676"}}>{dp("included", lang)}</FeeVal></FeeRow>
        <FeeRow>
          <FeeLabel style={{fontWeight:600,color:"#e8edf2"}}>{dp("total", lang)}</FeeLabel>
          <FeeVal style={{color:"#00e676",fontWeight:600}}>${parseFloat(amt).toFixed(2)}</FeeVal>
        </FeeRow>
      </div>
    )}

    {error && (
      <div style={{fontSize:".75rem",color:"#ff3b5c",background:"#ff3b5c11",
        border:"1px solid #ff3b5c33",borderRadius:"7px",padding:".6rem .8rem"}}>✕ {error}</div>
    )}

    <PayBtn disabled={!canPay||loading} onClick={handlePay}>
      {loading ? dp("initTingg", lang)
        : pays ? dpv("payViaTingg", lang, {country: country?.name})
        : dp("selectCountry", lang)}
    </PayBtn>

    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
      padding:".5rem .75rem",borderRadius:"8px",background:"#080a0c",border:"1px solid #1e2530"}}>
      <div style={{display:"flex",alignItems:"center",gap:".5rem"}}>
        <div style={{width:28,height:28,borderRadius:"6px",background:"#FF6B35",
          display:"flex",alignItems:"center",justifyContent:"center",
          fontFamily:"DM Mono,monospace",fontSize:".65rem",fontWeight:800,color:"#fff"}}>TG</div>
        <div>
          <div style={{fontSize:".7rem",fontWeight:600,color:"#e8edf2"}}>{dp("tinggDesc", lang)}</div>
          <div style={{fontSize:".6rem",color:"#5a6a7a"}}>{dp("tinggReach", lang)}</div>
        </div>
      </div>
      <a href="https://tingg.africa" target="_blank" rel="noreferrer"
        style={{fontSize:".62rem",color:"#4fc3f7",textDecoration:"none"}}>tingg.africa ↗</a>
    </div>

    <Notices>
      <Notice><span>📱</span><span>{dp("tinggNote1", lang)}</span></Notice>
      <Notice><span>⚡</span><span>{dp("tinggNote2", lang)}</span></Notice>
      <Notice><span>🌍</span><span>{dp("tinggNote3", lang)}</span></Notice>
    </Notices>
  </>);
}

/* ─── CreditBoostSection ──────────────────────────────────────────── */
const BoostWrap    = styled.div`display:flex;flex-direction:column;gap:1.1rem;`;
const BoostHero    = styled.div`
  background:linear-gradient(135deg,rgba(0,230,118,.06) 0%,rgba(79,195,247,.04) 100%);
  border:1px solid ${({theme})=>theme.green}33;border-radius:14px;
  padding:1.5rem;text-align:center;display:flex;flex-direction:column;
  align-items:center;gap:.75rem;
`;
const BoostIcon    = styled.div`font-size:2.5rem;`;
const BoostTitle   = styled.div`font-family:${({theme})=>theme.fontDisplay};font-size:1.1rem;font-weight:700;color:${({theme})=>theme.green};`;
const BoostDesc    = styled.div`font-size:.78rem;color:${({theme})=>theme.textMuted};line-height:1.6;max-width:280px;`;
const BoostAmount  = styled.div`
  font-family:${({theme})=>theme.fontMono};font-size:2rem;font-weight:700;
  color:${({theme})=>theme.green};letter-spacing:-.02em;
`;
const BoostSub     = styled.div`font-size:.68rem;color:${({theme})=>theme.textMuted};`;
const CondCard     = styled.div`
  background:${({theme})=>theme.bg};border:1px solid ${({theme})=>theme.border};
  border-radius:10px;padding:.85rem 1rem;display:flex;flex-direction:column;gap:.5rem;
`;
const CondRow      = styled.div`
  display:flex;align-items:center;gap:.65rem;
  font-size:.78rem;line-height:1.4;
`;
const CondIcon     = styled.span`
  font-size:.9rem;flex-shrink:0;
  width:24px;height:24px;border-radius:50%;
  display:flex;align-items:center;justify-content:center;
  background:${({$ok,theme})=>$ok?theme.greenDim:theme.redDim};
  border:1px solid ${({$ok,theme})=>$ok?theme.green+"44":theme.red+"44"};
`;
const CondText     = styled.span`color:${({$ok,theme})=>$ok?theme.text:theme.textMuted};`;
const TimerCard    = styled.div`
  background:${({theme})=>theme.bgHover};border:1px solid ${({theme})=>theme.border};
  border-radius:10px;padding:.85rem 1rem;
  display:flex;align-items:center;justify-content:space-between;gap:1rem;
`;
const TimerLeft    = styled.div`display:flex;flex-direction:column;gap:.2rem;`;
const TimerLabel   = styled.div`font-size:.72rem;color:${({theme})=>theme.textMuted};`;
const TimerVal     = styled.div`font-family:${({theme})=>theme.fontMono};font-size:.9rem;font-weight:600;color:${({theme})=>theme.accent};`;
const StatusPill   = styled.div`
  font-family:${({theme})=>theme.fontMono};font-size:.62rem;padding:.25rem .65rem;
  border-radius:100px;letter-spacing:.06em;
  background:${({$status,theme})=>$status==="approved"?theme.greenDim:$status==="pending"?"rgba(255,193,7,.1)":theme.redDim};
  color:${({$status,theme})=>$status==="approved"?theme.green:$status==="pending"?theme.gold:theme.red};
  border:1px solid ${({$status,theme})=>$status==="approved"?theme.green+"44":$status==="pending"?theme.gold+"44":theme.red+"44"};
`;
const shimmerCredit = keyframes`0%{background-position:-400px 0}100%{background-position:400px 0}`;
const BoostSkel    = styled.div`
  height:${({$h})=>$h||"48px"};border-radius:10px;
  background:linear-gradient(90deg,${({theme})=>theme.bgCard} 25%,${({theme})=>theme.bgHover} 50%,${({theme})=>theme.bgCard} 75%);
  background-size:400px 100%;animation:${shimmerCredit} 1.4s infinite;
`;

function CreditBoostSection({ onSuccess, lang }) {
  const [wallet,       setWallet]       = useState(null);
  const [positions,    setPositions]    = useState([]);
  const [existing,     setExisting]     = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [wRes, pRes] = await Promise.all([
          fetchWallet(),
          fetchMyPositions(),
        ]);
        setWallet(wRes.data);
        setPositions(pRes.data?.results || pRes.data || []);
      } catch {}

      try {
        const cRes = await getCreditStatus();
        if (cRes.status === 204 || !cRes.data || !cRes.data.id) {
          setExisting(null);
        } else {
          setExisting(cRes.data);
        }
      } catch {
        setExisting(null);
      }

      setLoading(false);
    };
    load();
  }, []);

  const balance      = parseFloat(wallet?.usdc_balance || 0);
  // ⚠ Filtre robuste : ignore les positions "zombies" (status=OPEN mais shares=0)
  const openTrades   = positions.filter(p => p.status === "OPEN" && parseFloat(p.shares || 0) > 0);
  const condBalance  = balance < 100;
  const condTrades   = openTrades.length === 0;
  const canRequest   = condBalance && condTrades && !existing;
  const creditAmount = Math.max(0, 100 - balance).toFixed(2);

  const pendingUntil = existing?.created_at
    ? new Date(new Date(existing.created_at).getTime() + 24 * 3600 * 1000)
    : null;
  const [countdown, setCountdown] = useState("");
  useEffect(() => {
    if (!pendingUntil) return;
    const tick = () => {
      const diff = pendingUntil - Date.now();
      if (diff <= 0) { setCountdown(dp("processingLabel", lang)); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(`${h}h ${m.toString().padStart(2,"0")}m ${s.toString().padStart(2,"0")}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [pendingUntil, lang]);

  const handleRequest = async () => {
    if (!canRequest || submitting) return;
    setSubmitting(true); setError("");
    try {
      const res = await requestCredit();
      setExisting(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || dp("requestErrorFallback", lang));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <BoostWrap>
      <BoostSkel $h="140px"/>
      <BoostSkel $h="80px"/>
      <BoostSkel $h="48px"/>
    </BoostWrap>
  );

  // ── Demande en cours ─────────────────────────────────────────────
  if (existing) return (
    <BoostWrap>
      <BoostHero>
        <BoostIcon>{existing.status === "approved" ? "✅" : "⏳"}</BoostIcon>
        <BoostTitle>
          {existing.status === "approved" ? dp("creditApproved", lang) : dp("creditPending", lang)}
        </BoostTitle>
        <BoostDesc>
          {existing.status === "approved"
            ? dp("creditApprovedDesc", lang)
            : dp("creditPendingDesc", lang)}
        </BoostDesc>
        <StatusPill $status={existing.status || "pending"}>
          {existing.status === "approved" ? dp("statusApproved", lang) : dp("statusPending", lang)}
        </StatusPill>
      </BoostHero>

      {existing.status !== "approved" && pendingUntil && (
        <TimerCard>
          <TimerLeft>
            <TimerLabel>{dp("timerLabel", lang)}</TimerLabel>
            <TimerVal>{countdown}</TimerVal>
          </TimerLeft>
          <div style={{fontSize:"1.5rem"}}>🕐</div>
        </TimerCard>
      )}

      <CondCard>
        <CondRow>
          <CondIcon $ok><span style={{fontSize:".65rem"}}>📅</span></CondIcon>
          <CondText $ok>
            {dp("requestSentOn", lang)}{" "}
            <strong>{fmtDateSafe(existing.created_at)}</strong>
          </CondText>
        </CondRow>
        <CondRow>
          <CondIcon $ok><span style={{fontSize:".65rem"}}>💰</span></CondIcon>
          <CondText $ok>{dp("amountLabel", lang)} : <strong>100 EVP</strong></CondText>
        </CondRow>
      </CondCard>

      {existing.status === "approved" && (
        <PayBtn onClick={onSuccess} style={{background:"#00e676",color:"#000"}}>
          {dp("doneClose", lang)}
        </PayBtn>
      )}
    </BoostWrap>
  );

  // ── Formulaire ────────────────────────────────────────────────────
  return (
    <BoostWrap>
      <BoostHero>
        <BoostIcon>🎁</BoostIcon>
        <BoostTitle>{dp("boostTitle", lang)}</BoostTitle>
        <BoostDesc>{dp("boostDesc", lang)}</BoostDesc>
        <BoostAmount>+{creditAmount} EVP</BoostAmount>
        <BoostSub>{dp("boostAmountSub", lang)}</BoostSub>
      </BoostHero>

      <CondCard>
        <div style={{fontSize:".68rem",textTransform:"uppercase",letterSpacing:".08em",
          color:"#5a6a7a",marginBottom:".25rem",fontFamily:"DM Mono,monospace"}}>
          {dp("eligibility", lang)}
        </div>
        <CondRow>
          <CondIcon $ok={condBalance}>
            <span style={{fontSize:".65rem"}}>{condBalance ? "✓" : "✕"}</span>
          </CondIcon>
          <CondText $ok={condBalance}>
            {dp("condBalance", lang)}
            <span style={{marginLeft:".4rem",fontFamily:"DM Mono,monospace",
              color:condBalance?"#00e676":"#ff3b5c"}}>
              {dpv("condBalanceCur", lang, {amount: balance.toFixed(2)})}
            </span>
          </CondText>
        </CondRow>
        <CondRow>
          <CondIcon $ok={condTrades}>
            <span style={{fontSize:".65rem"}}>{condTrades ? "✓" : "✕"}</span>
          </CondIcon>
          <CondText $ok={condTrades}>
            {dp("condNoTrades", lang)}
            {!condTrades && (
              <span style={{marginLeft:".4rem",color:"#ff3b5c",fontFamily:"DM Mono,monospace"}}>
                ({openTrades.length} {openTrades.length > 1 ? dp("positionMany", lang) : dp("positionOne", lang)})
              </span>
            )}
          </CondText>
        </CondRow>
      </CondCard>

      <TimerCard>
        <TimerLeft>
          <TimerLabel>{dp("processingTime", lang)}</TimerLabel>
          <TimerVal>{dp("within24h", lang)}</TimerVal>
        </TimerLeft>
        <div style={{fontSize:"1.4rem"}}>⏱</div>
      </TimerCard>

      {!canRequest && (
        <div style={{fontSize:".75rem",color:"#ffc107",background:"rgba(255,193,7,.06)",
          border:"1px solid rgba(255,193,7,.25)",borderRadius:"8px",padding:".65rem .85rem",lineHeight:1.5}}>
          {!condBalance && !condTrades
            ? dp("warnBoth", lang)
            : !condBalance
            ? dp("warnBalance", lang)
            : dp("warnTrades", lang)}
        </div>
      )}

      {error && (
        <div style={{fontSize:".75rem",color:"#ff3b5c",background:"#ff3b5c11",
          border:"1px solid #ff3b5c33",borderRadius:"8px",padding:".6rem .85rem"}}>
          ✕ {error}
        </div>
      )}

      <PayBtn disabled={!canRequest || submitting} onClick={handleRequest}
        style={canRequest ? {background:"#00e676",color:"#000"} : {}}>
        {submitting ? dp("sendingRequest", lang) : canRequest
          ? dpv("requestEVP", lang, {amount: creditAmount})
          : dp("condsNotMet", lang)}
      </PayBtn>

      <Notices>
        <Notice><span>ℹ️</span><span>{dp("boostNote1", lang)}</span></Notice>
        <Notice><span>🔒</span><span>{dp("boostNote2", lang)}</span></Notice>
      </Notices>
    </BoostWrap>
  );
}

/* ─── Main export ────────────────────────────────────────────────── */
export default function DepositModal({ onClose }) {
  const { lang } = useI18n();
  const [method,  setMethod]  = useState("boost");
  const [success, setSuccess] = useState(null);

  const [mobilePays, setMobilePays] = useState(null);
  const [mobileAmt,  setMobileAmt]  = useState("10");

  const [coin,    setCoin]    = useState("USDC");
  const [network, setNetwork] = useState(null);
  const [address, setAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied,  setCopied]  = useState(false);

  const [cardAmt, setCardAmt] = useState("100");

  const [bankAmt,    setBankAmt]    = useState("100");
  const [bankRefCode]               = useState(genRef);
  const [copiedBank, setCopiedBank] = useState("");

  const coinCfg = COINS.find(c => c.id === coin);

  useEffect(()=>{ setNetwork(coinCfg.networks[0]); setAddress(null); }, [coin]);

  const loadAddress = useCallback(async()=>{
    if (!network) return;
    setLoading(true);
    try {
      const res = await fetchDepositAddress(coin);
      setAddress(res.data.address);
    } catch {
      const key=`${coin}_${network}`;
      setAddress(MOCK_ADDRESSES[key]||MOCK_ADDRESSES[`${coin}_${coinCfg.networks[0]}`]||"0xMock");
    } finally { setLoading(false); }
  }, [coin, network, coinCfg]);

  useEffect(()=>{ if (method==="crypto") loadAddress(); }, [loadAddress, method]);

  const copy = (text, key="main") => {
    navigator.clipboard.writeText(text).catch(()=>{});
    if (key==="main") { setCopied(true); setTimeout(()=>setCopied(false),2000); }
    else { setCopiedBank(key); setTimeout(()=>setCopiedBank(""),2000); }
  };

  const handleBankConfirm = () => setSuccess({method:"bank", amount:bankAmt});

  if (success) return (
    <Overlay onClick={e=>e.target===e.currentTarget&&onClose()}>
      <Modal>
        <Head>
          <div><ModalTitle>{dp("successDeposit", lang)} <span>{dp("successConfirmed", lang)}</span></ModalTitle></div>
          <CloseBtn onClick={onClose}>✕</CloseBtn>
        </Head>
        <SuccessWrap>
          <CheckCircle>✓</CheckCircle>
          <SuccessTitle>
            {success.method==="card"   ? dp("successCard",   lang)
           : success.method==="mobile" ? dp("successMobile", lang)
           : success.method==="boost"  ? dp("successBoost",  lang)
           :                             dp("successBank",   lang)}
          </SuccessTitle>
          <SuccessSub>
            {success.method==="card"
              ? dpv("successCardDesc", lang, {amount: success.amount})
              : success.method==="mobile"
              ? dp("successMobileDesc", lang)
              : success.method==="boost"
              ? dp("successBoostDesc", lang)
              : dpv("successBankDesc", lang, {amount: success.amount})}
          </SuccessSub>
          <PayBtn onClick={onClose} style={{maxWidth:200,marginTop:".5rem"}}>{dp("close",lang)}</PayBtn>
        </SuccessWrap>
      </Modal>
    </Overlay>
  );

  return (
    <Overlay onClick={e=>e.target===e.currentTarget&&onClose()}>
      <Modal>
        <Head>
          <div>
            <ModalTitle>{dp("title",lang)} <span>{dp("title2",lang)}</span></ModalTitle>
            {false && <ModalSub>{dp("sub",lang)}</ModalSub>}
          </div>
          <CloseBtn onClick={onClose}>✕</CloseBtn>
        </Head>

        <Body>
{false &&          <MethodTabs>
            {[
              {id:"boost", icon:"🎁"},
              // {id:"crypto",icon:"₿"},
              // {id:"card",  icon:"💳"},
              // {id:"mobile",icon:"📱"},
              // {id:"bank",  icon:"🏦"},
            ].map(m=>(
              <MethodTab key={m.id} $active={method===m.id} onClick={()=>setMethod(m.id)}>
                <TabIcon>{m.icon}</TabIcon>
                <TabLabel $active={method===m.id}>{dp(m.id,lang)}</TabLabel>
              </MethodTab>
            ))}
          </MethodTabs>}

          {method==="boost" && (
            <CreditBoostSection
              lang={lang}
              onSuccess={() => setSuccess({method:"boost", amount:"100"})}
            />
          )}

          {method==="crypto" && <>
            <div>
              <SectionLabel>{dp("selectCrypto", lang)}</SectionLabel>
              <CoinGrid>
                {COINS.map(c=>(
                  <CoinBtn key={c.id} $active={coin===c.id} onClick={()=>setCoin(c.id)}>
                    <CoinIcon>{c.icon}</CoinIcon>
                    <CoinName $active={coin===c.id}>{c.label}</CoinName>
                    <CoinPrice>{c.minDeposit} min</CoinPrice>
                  </CoinBtn>
                ))}
              </CoinGrid>
            </div>
            <div>
              <SectionLabel>{dp("network", lang)}</SectionLabel>
              <NetRow>
                {coinCfg.networks.map(n=>(
                  <NetBadge key={n} $active={network===n} onClick={()=>setNetwork(n)}>{n}</NetBadge>
                ))}
              </NetRow>
            </div>
            <QRBlock>
              {loading?(<><Spinner/><LoadingLabel>{dp("genAddr",lang)}</LoadingLabel></>):
              address?(<>
                <QRWrap><QRScanLine/><QRCode address={address}/></QRWrap>
                <AddressBox>
                  <AddressText title={address}>{address}</AddressText>
                  <CopyBtn onClick={()=>copy(address)}>{copied?dp("copied",lang):dp("copy",lang)}</CopyBtn>
                </AddressBox>
                <MetaRow><MetaKey>{dp("minDep",lang)}</MetaKey><MetaVal>{coinCfg.minDeposit} {coin}</MetaVal></MetaRow>
                <MetaRow><MetaKey>{dp("confirmations",lang)}</MetaKey><MetaVal>{coinCfg.confirmations}</MetaVal></MetaRow>
                <MetaRow><MetaKey>{dp("network",lang)}</MetaKey><MetaVal>{network}</MetaVal></MetaRow>
              </>):null}
            </QRBlock>
            <Notices>
              <Notice><span>⚠️</span><span>{dpv("cryptoNote1", lang, {coin, network})}</span></Notice>
              <Notice><span>⏱</span><span>{dpv("cryptoNote2", lang, {n: coinCfg.confirmations})}</span></Notice>
            </Notices>
          </>}

          {method==="card" && <StripeCardSection
            lang={lang}
            amt={cardAmt} setAmt={setCardAmt}
            onSuccess={()=>setSuccess({method:"card", amount:cardAmt})}
          />}

          {method==="mobile" && <MobileMoney
            lang={lang}
            pays={mobilePays} setPays={setMobilePays}
            amt={mobileAmt}   setAmt={setMobileAmt}
            onSuccess={()=>setSuccess({method:"mobile", amount:mobileAmt})}
          />}

          {method==="bank" && <>
            <div>
              <SectionLabel>{dp("bankAmount", lang)}</SectionLabel>
              <AmountPresets>
                {[100,250,500,1000,2500].map(v=>(
                  <AmountChip key={v} $active={bankAmt===String(v)} onClick={()=>setBankAmt(String(v))}>${v}</AmountChip>
                ))}
              </AmountPresets>
              <FieldInput style={{marginTop:".5rem"}} type="number" min="100"
                placeholder={dp("bankAmountPh", lang)} value={bankAmt}
                onChange={e=>setBankAmt(e.target.value)}/>
            </div>
            <div>
              <SectionLabel>{dp("bankDetails", lang)}</SectionLabel>
              <BankCard>
                {[
                  {key:dp("bankBeneficiary", lang), val:"PredictX SAS",                     id:"bene"},
                  {key:dp("bankIBAN",        lang), val:"FR76 3000 4000 0300 0000 3456 789", id:"iban"},
                  {key:dp("bankBIC",         lang), val:"BNPAFRPPXXX",                       id:"bic"},
                  {key:dp("bankName",        lang), val:"BNP Paribas",                       id:"bank"},
                  {key:dp("bankCountry",     lang), val:"France 🇫🇷",                       id:null},
                ].map(row=>(
                  <BankRow key={row.key}>
                    <BankKey>{row.key}</BankKey>
                    <BankVal>
                      <span>{row.val}</span>
                      {row.id && <SmallCopy onClick={()=>copy(row.val,row.id)}>{copiedBank===row.id?"✓":dp("copy", lang)}</SmallCopy>}
                    </BankVal>
                  </BankRow>
                ))}
              </BankCard>
            </div>
            <div>
              <SectionLabel>{dp("bankRef", lang)}</SectionLabel>
              <RefBadge>
                <RefCode>{bankRefCode}</RefCode>
                <RefSub>{dp("bankRefDesc", lang)}</RefSub>
                <SmallCopy style={{marginTop:".5rem"}} onClick={()=>copy(bankRefCode,"ref")}>
                  {copiedBank==="ref" ? dp("copied", lang) : dp("copyReference", lang)}
                </SmallCopy>
              </RefBadge>
            </div>
            <FeeRow><FeeLabel>{dp("creditDelay", lang)}</FeeLabel><FeeVal>{dp("workingDays", lang)}</FeeVal></FeeRow>
            <FeeRow><FeeLabel>{dp("fees", lang)}</FeeLabel><FeeVal style={{color:"#00e676"}}>{dp("free", lang)}</FeeVal></FeeRow>
            <FeeRow><FeeLabel>{dp("minAmount", lang)}</FeeLabel><FeeVal>$100</FeeVal></FeeRow>
            <PayBtn disabled={parseFloat(bankAmt)<100} onClick={handleBankConfirm}>
              {dp("bankConfirm", lang)}
            </PayBtn>
            <Notices>
              <Notice><span>ℹ️</span><span>{dp("bankNote1", lang)}</span></Notice>
              <Notice><span>⚠️</span><span>{dpv("bankNote2", lang, {ref: bankRefCode})}</span></Notice>
            </Notices>
          </>}

        </Body>
      </Modal>
    </Overlay>
  );
}
